import {Component, isDevMode, OnInit} from '@angular/core';
import {AngularFirestore} from "@angular/fire/compat/firestore";
import {AngularFireDatabase} from "@angular/fire/compat/database";
import {environment} from "../../environments/environment";


import * as XLSX from 'xlsx';
// import {hospital} from "@igniteui/material-icons-extended";
import {Centre} from "../interfaces/Centres";
import {AdmissionLogService} from "../services/admission-log.service";
import {Timestamp} from "firebase/firestore";
import {TasksService} from "../modules/task/tasks.service";
import {Task} from "../modules/task/models/task.model";
import {AngularFireFunctions} from "@angular/fire/compat/functions";
import {TriggerService} from "../modules/task/trigger.service";
import {
  childGroupSessionAttendanceCheckCallable,
  counsellingIndividualDocumentCheckCallable, groupCounsellingSessionCheckCallable, groupSessionDocumentCheckCallable
} from "../../../functions/src/counselling.triggers";
import {Functions, httpsCallable} from "@angular/fire/functions";
import {NotificationService} from "../services/notification.service";

@Component({
  selector: 'app-code',
  templateUrl: './code.component.html',
  styleUrls: ['./code.component.scss']
})
export class CodeComponent implements OnInit {
  env = isDevMode() ? environment.testMode : environment.productionMode;
  mData: any[] = []
  tableData: any[] = []
  userUid: string = '';
  newUserEmail: string = '';
  sourceCollection: string = '';
  targetCollection: string = '';
  downloadCollectionPath: string = '';
  downloadDocumentId: string = '';
  downloadFieldPath: string = '';
  uploadCollectionPath: string = '';
  uploadDocumentId: string = '';
  uploadMergeData: boolean = false;
  inventoryMigrationDocId: string = '';
  inventoryUpdateDocId: string = '';

  // ✅ Education Monthly Summary backfill
  isBuildingSummary = false;
  summaryBuildStatus = '';
  summaryBuildProgress = 0;

  // ✅ Counselling Monthly Summary backfill
  isBuildingCounsellingSummary = false;
  counsellingSummaryBuildStatus = '';
  counsellingSummaryBuildProgress = 0;

  // ✅ Admission Cycle Logs backfill
  isBuildingAdmissionCycles = false;
  admissionCycleBuildStatus = '';
  admissionCycleBuildProgress = 0;

  // Hospitals RTDB -> Firestore migration
  isMigratingHospitals = false;
  hospitalMigrationStatus = '';

  // Units RTDB -> Firestore migration
  isMigratingUnits = false;
  unitMigrationStatus = '';
  isVerifyingUnits = false;
  unitVerifyStatus = '';

  // DevEntries state
  isRunningDevEntries = false;
  devEntriesStatus = '';
  devEntriesProgress = 0;

  constructor(private readonly mFirestore: AngularFirestore,
              private readonly mDatabase: AngularFireDatabase,
              private readonly mTaskService: TasksService,
              private readonly fns: AngularFireFunctions, // <--- Inject AngularFireFunctions (compat)
              private readonly functions: Functions, // <--- Inject Functions (modular)
              private readonly mTriggerService: TriggerService,
              private  readonly mNotifications:NotificationService,
              private readonly mLogService: AdmissionLogService) {

    // Commented out to use production functions instead of emulator
    // this.fns.useEmulator('localhost', 5001);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Migration: RTDB "Hospitals" -> Firestore hospitals (dev + prod, identical data)
  // ─────────────────────────────────────────────────────────────────────────
  async migrateHospitalsToFirestore() {
    if (this.isMigratingHospitals) return;

    const targets = [environment.testMode.hospitals, environment.productionMode.hospitals];
    if (!confirm(
      `This copies every hospital from the Realtime Database "Hospitals" node into the Firestore ` +
      `collections ${targets.map(t => `"${t}"`).join(' and ')}.\n\n` +
      'Both collections get identical data (document id = RTDB key). Existing documents with the ' +
      'same id are overwritten; the Realtime Database is not modified.\n\nContinue?')) return;

    this.isMigratingHospitals = true;
    this.hospitalMigrationStatus = '⏳ Reading hospitals from Realtime Database…';
    try {
      const snap = await this.mDatabase.database.ref('Hospitals').once('value');
      const toArray = (v: any): any[] => v == null ? [] : (Array.isArray(v) ? v.filter(x => x != null) : Object.values(v));
      const arrayFields = ['drDetails', 'socialWorkerDetails', 'adminDetails', 'notes', 'isAssociateCenter', 'removedDrDetails'];

      const docs: { id: string; data: any }[] = [];
      let withoutCentres = 0;
      snap.forEach(child => {
        const raw = child.val();
        if (!raw || typeof raw !== 'object') return;
        const {documents, ...hospital} = raw; // uploaded documents stay in RTDB
        arrayFields.forEach(f => {
          if (hospital[f] !== undefined) hospital[f] = toArray(hospital[f]);
        });
        if (!Array.isArray(hospital.forCentres) || !hospital.forCentres.length) {
          hospital.forCentres = Array.from(new Set(
            (hospital.isAssociateCenter || []).map((c: any) => c?.centreCode || c).filter(Boolean)
          ));
        }
        if (!hospital.forCentres.length) withoutCentres++;
        hospital.id = child.key;
        docs.push({id: child.key as string, data: JSON.parse(JSON.stringify(hospital))});
      });

      const db = this.mFirestore.firestore;
      let written = 0;
      for (const target of targets) {
        for (let i = 0; i < docs.length; i += 400) {
          const batch = db.batch();
          docs.slice(i, i + 400).forEach(d => batch.set(db.collection(target).doc(d.id), d.data));
          await batch.commit();
          written += Math.min(400, docs.length - i);
          this.hospitalMigrationStatus = `⏳ Writing to ${target}… (${written}/${docs.length * targets.length})`;
        }
      }

      this.hospitalMigrationStatus = `✅ Migrated ${docs.length} hospitals to ${targets.join(' and ')}.` +
        (withoutCentres ? `\n⚠️ ${withoutCentres} hospitals have no centres and won't appear in centre-filtered lists until edited.` : '');
    } catch (e: any) {
      this.hospitalMigrationStatus = `❌ Error: ${e?.message ?? e}`;
      console.error('migrateHospitalsToFirestore failed:', e);
    } finally {
      this.isMigratingHospitals = false;
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Migration: RTDB units (env.unitsRtdb) -> Firestore units (env.units/{centre}/units/{key})
  // ─────────────────────────────────────────────────────────────────────────
  private get unitEnvTargets() {
    return [
      {label: 'prod', rtdb: environment.productionMode.unitsRtdb, fs: environment.productionMode.units, child: environment.productionMode.child},
      {label: 'dev', rtdb: environment.testMode.unitsRtdb, fs: environment.testMode.units, child: environment.testMode.child},
    ];
  }

  /** RTDB `{path}/{centre}/{unitKey}` -> Map<centre, Map<unitKey, unit>>. Skips stray non-unit nodes. */
  private async readRtdbUnits(path: string): Promise<Map<string, Map<string, any>>> {
    const snap = await this.mDatabase.database.ref(path).once('value');
    const result = new Map<string, Map<string, any>>();
    snap.forEach(centreSnap => {
      const centreCode = centreSnap.key as string;
      // `test` / `units` are stray nodes written by old centre.service.ts code
      if (centreCode === 'test' || centreCode === 'units') return;
      const units = new Map<string, any>();
      centreSnap.forEach(unitSnap => {
        const v = unitSnap.val();
        if (!v || typeof v !== 'object' || typeof v.status !== 'string') return;
        units.set(unitSnap.key as string, JSON.parse(JSON.stringify({...v, key: v.key ?? unitSnap.key})));
      });
      if (units.size) result.set(centreCode, units);
    });
    return result;
  }

  private async readFirestoreUnits(collection: string, centreCodes: Iterable<string>): Promise<Map<string, Map<string, any>>> {
    const db = this.mFirestore.firestore;
    const result = new Map<string, Map<string, any>>();
    for (const centreCode of centreCodes) {
      const snap = await db.collection(`${collection}/${centreCode}/units`).get();
      const units = new Map<string, any>();
      snap.forEach(d => units.set(d.id, d.data()));
      result.set(centreCode, units);
    }
    return result;
  }

  async migrateUnitsToFirestore() {
    if (this.isMigratingUnits) return;

    const targets = this.unitEnvTargets;
    if (!confirm(
      'This copies every unit from the Realtime Database into Firestore:\n' +
      targets.map(t => `  • RTDB "${t.rtdb}" → Firestore "${t.fs}/{centre}/units/{unit}" (${t.label})`).join('\n') +
      '\n\nExisting Firestore unit documents with the same id are overwritten with the RTDB data; ' +
      'the Realtime Database is not modified.\n\nContinue?')) return;

    this.isMigratingUnits = true;
    this.unitMigrationStatus = '⏳ Reading units from Realtime Database…';
    try {
      const db = this.mFirestore.firestore;
      const lines: string[] = [];
      for (const t of targets) {
        const byCentre = await this.readRtdbUnits(t.rtdb);
        const docs: { path: string; data: any }[] = [];
        byCentre.forEach((units, centreCode) =>
          units.forEach((data, key) => docs.push({path: `${t.fs}/${centreCode}/units/${key}`, data})));

        for (let i = 0; i < docs.length; i += 400) {
          const batch = db.batch();
          docs.slice(i, i + 400).forEach(d => batch.set(db.doc(d.path), d.data));
          await batch.commit();
          this.unitMigrationStatus = `⏳ Writing ${t.label} units to ${t.fs}… (${Math.min(i + 400, docs.length)}/${docs.length})`;
        }
        lines.push(`✅ ${t.label}: ${docs.length} units in ${byCentre.size} centres → ${t.fs}`);
      }
      this.unitMigrationStatus = lines.join('\n') + '\nRun "Verify Units" next.';
    } catch (e: any) {
      this.unitMigrationStatus = `❌ Error: ${e?.message ?? e}`;
      console.error('migrateUnitsToFirestore failed:', e);
    } finally {
      this.isMigratingUnits = false;
    }
  }

  /** Read-only: RTDB vs Firestore per centre (count + field diff), then units <-> children reconciliation. */
  async verifyUnitsMigration() {
    if (this.isVerifyingUnits) return;
    this.isVerifyingUnits = true;
    this.unitVerifyStatus = '⏳ Verifying…';
    const MAX_LINES = 40;
    try {
      const out: string[] = [];
      const db = this.mFirestore.firestore;

      for (const t of this.unitEnvTargets) {
        this.unitVerifyStatus = `⏳ Verifying ${t.label}: reading units…`;
        const rtdb = await this.readRtdbUnits(t.rtdb);
        const fs = await this.readFirestoreUnits(t.fs, rtdb.keys());

        // ── 1. counts + field diffs ──
        const countIssues: string[] = [];
        const fieldDiffs: string[] = [];
        let rtdbTotal = 0, fsTotal = 0;
        rtdb.forEach((units, centreCode) => {
          const fsUnits = fs.get(centreCode)!;
          rtdbTotal += units.size;
          fsTotal += fsUnits.size;
          if (units.size !== fsUnits.size) {
            countIssues.push(`${centreCode}: RTDB ${units.size} vs Firestore ${fsUnits.size}`);
          }
          units.forEach((r, key) => {
            const f = fsUnits.get(key);
            if (!f) {
              fieldDiffs.push(`${centreCode}/${key}: missing in Firestore`);
              return;
            }
            // getUnit() orders by `order`, which silently drops docs that lack it
            if (typeof f.order !== 'number') fieldDiffs.push(`${centreCode}/${key}: Firestore doc has no numeric "order" (won't be listed)`);
            new Set([...Object.keys(r), ...Object.keys(f)]).forEach(field => {
              if (JSON.stringify(r[field]) !== JSON.stringify(f[field])) {
                fieldDiffs.push(`${centreCode}/${key}.${field}: RTDB ${JSON.stringify(r[field])} vs Firestore ${JSON.stringify(f[field])}`);
              }
            });
          });
        });

        // ── 2. reconcile with children (report only) ──
        this.unitVerifyStatus = `⏳ Verifying ${t.label}: reading children…`;
        const childSnap = await db.collection(t.child).get();
        const children = new Map<string, any>();
        childSnap.forEach(d => children.set(d.id, d.data()));

        const unitIssues: string[] = [];
        fs.forEach((units, centreCode) => units.forEach((u, key) => {
          if (u.status !== 'Alloted') return;
          const c = children.get(u.allotedTo);
          if (!c) unitIssues.push(`${centreCode}/${key}: Alloted to ${u.allotedTo}, child not found`);
          else if (c.isAlloted !== true) unitIssues.push(`${centreCode}/${key}: Alloted to ${u.allotedTo}, but child isAlloted=${c.isAlloted}`);
          else if (c.selectUnit !== key || c.centreCode !== centreCode) {
            unitIssues.push(`${centreCode}/${key}: Alloted to ${u.allotedTo}, but child is in ${c.centreCode}/${c.selectUnit}`);
          }
        }));
        const childIssues: string[] = [];
        children.forEach((c, childCode) => {
          if (c.isAlloted !== true) return;
          const u = fs.get(c.centreCode)?.get(c.selectUnit);
          if (!u) childIssues.push(`${childCode}: isAlloted in ${c.centreCode}/${c.selectUnit}, unit not found`);
          else if (u.status !== 'Alloted' || u.allotedTo !== childCode) {
            childIssues.push(`${childCode}: isAlloted in ${c.centreCode}/${c.selectUnit}, unit is ${u.status} (allotedTo ${u.allotedTo})`);
          }
        });

        const section = (title: string, items: string[]) => {
          out.push(`${items.length ? '⚠️' : '✅'} ${title}: ${items.length}`);
          items.slice(0, MAX_LINES).forEach(i => out.push(`    ${i}`));
          if (items.length > MAX_LINES) out.push(`    … ${items.length - MAX_LINES} more (see console)`);
          if (items.length) console.log(`[verifyUnits ${t.label}] ${title}`, items);
        };
        out.push(`── ${t.label}: ${rtdb.size} centres, RTDB ${rtdbTotal} units / Firestore ${fsTotal} units ──`);
        section('Centres with count mismatch', countIssues);
        section('Field diffs / missing units', fieldDiffs);
        section('Alloted units without a matching child', unitIssues);
        section('Allotted children without a matching unit', childIssues);
      }
      this.unitVerifyStatus = out.join('\n');
    } catch (e: any) {
      this.unitVerifyStatus = `❌ Error: ${e?.message ?? e}`;
      console.error('verifyUnitsMigration failed:', e);
    } finally {
      this.isVerifyingUnits = false;
    }
  }

  runBulkAdmissionLogsCreation() {
    console.log('processing Admission Logs in code component')
    this.mLogService.CreateAdmissionLogs()
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Backfill: Bulk-create ALAdmissionCycles + update ALChild.lastDocId
  // ─────────────────────────────────────────────────────────────────────────
  async runAdmissionCycleLogs() {
    if (this.isBuildingAdmissionCycles) return;

    const confirmed = confirm(
      '⚠️ This will read ALL ALChild documents and write AdmissionCycle entries\n' +
      'to the ALAdmissionCycles collection.\n\n' +
      'Each child\'s ALChild document will also be updated with lastDocId\n' +
      'pointing to their most recent cycle.\n\n' +
      'Safe to re-run — existing cycle docs will be overwritten.\n\nContinue?'
    );
    if (!confirmed) return;

    this.isBuildingAdmissionCycles = true;
    this.admissionCycleBuildStatus = '⏳ Starting…';
    this.admissionCycleBuildProgress = 0;

    try {
      this.admissionCycleBuildStatus = '📋 Fetching children from ALChild…';
      await this.mLogService.createAdmissionCycleLogs('Admin-Backfill');
      this.admissionCycleBuildStatus = '✅ Admission Cycle Logs created successfully!';
      this.admissionCycleBuildProgress = 100;
    } catch (e: any) {
      this.admissionCycleBuildStatus = `❌ Error: ${e?.message ?? e}`;
      console.error('runAdmissionCycleLogs failed:', e);
    } finally {
      this.isBuildingAdmissionCycles = false;
    }
  }

  getTotalNumberOfAdmissionLogs() {
    this.mLogService.getTotalAdmissionLogsCount().then(
      (count) => {
        console.log('Total Admission Logs Count:', count?.docs.length)
      }
    )
  }

  async bulkAddAgeInEducationSessions() {
    console.log('Starting bulk age addition to education sessions...');

    try {
      // Step 1: Fetch ALL children from ALChild collection first
      console.log('Fetching all children from ALChild...');
      const childrenSnapshot = await this.mFirestore.collection(this.env.child).get().toPromise();

      if (!childrenSnapshot || childrenSnapshot.empty) {
        console.warn('ALChild collection is empty');
        alert('No children found in database');
        return;
      }

      // Step 2: Build child data cache with age and dob
      const childDataCache = new Map<string, { dob: any; age: number }>();
      let childrenProcessed = 0;
      let childrenWithoutDob = 0;

      childrenSnapshot.docs.forEach(childDoc => {
        const childData = childDoc.data() as any;
        const childCode = childDoc.id;
        const dob = childData.dob;

        if (dob) {
          const age = this.calculateAgeFromDob(dob);
          childDataCache.set(childCode, { dob, age });
          childrenProcessed++;
        } else {
          console.warn(`Child ${childCode} has no DOB`);
          childrenWithoutDob++;
        }
      });

      console.log(`Children cache built: ${childrenProcessed} children with DOB, ${childrenWithoutDob} without DOB`);

      // Step 3: Fetch all sessions from educationTest collection
      console.log('Fetching all education sessions...');
      const sessionsSnapshot = await this.mFirestore.collection(this.env.education).get().toPromise();

      if (!sessionsSnapshot || sessionsSnapshot.empty) {
        console.warn('educationTest collection is empty');
        alert('No education sessions found');
        return;
      }

      const totalSessions = sessionsSnapshot.docs.length;
      console.log(`Found ${totalSessions} education sessions to update`);

      // Step 4: Process and update sessions
      let batch = this.mFirestore.firestore.batch();
      let batchCount = 0;
      let updatedCount = 0;
      let skippedCount = 0;
      let childNotFoundCount = 0;
      const batchSize = 500;

      for (const sessionDoc of sessionsSnapshot.docs) {
        const sessionData = sessionDoc.data() as any;
        const childCode = sessionData.childCode;

        if (!childCode) {
          console.warn(`Session ${sessionDoc.id} has no childCode, skipping...`);
          skippedCount++;
          continue;
        }

        // Check if age and dob already exist
        if (sessionData.age !== undefined && sessionData.dob !== undefined) {
          console.log(`Session ${sessionDoc.id} already has age and dob, skipping...`);
          skippedCount++;
          continue;
        }

        // Get child info from cache
        const childInfo = childDataCache.get(childCode);

        if (!childInfo) {
          console.warn(`Child with code ${childCode} not found in cache, skipping session ${sessionDoc.id}`);
          childNotFoundCount++;
          skippedCount++;
          continue;
        }

        // Update session with age and dob
        const updateData = {
          age: childInfo.age,
          dob: childInfo.dob
        };

        batch.update(sessionDoc.ref, updateData);
        batchCount++;
        updatedCount++;

        console.log(`✓ Will update session ${sessionDoc.id} for child ${childCode} | Age: ${childInfo.age}`);

        // Commit batch when reaching batch size
        if (batchCount >= batchSize) {
          await batch.commit();
          console.log(`Progress: ${updatedCount}/${totalSessions} sessions updated`);
          batch = this.mFirestore.firestore.batch();
          batchCount = 0;
        }
      }

      // Commit remaining updates
      if (batchCount > 0) {
        await batch.commit();
      }

      console.log(`\n=== Education Sessions Age Update Summary ===`);
      console.log(`Total children in database: ${childrenSnapshot.docs.length}`);
      console.log(`Children with DOB: ${childrenProcessed}`);
      console.log(`Children without DOB: ${childrenWithoutDob}`);
      console.log(`Total sessions: ${totalSessions}`);
      console.log(`Sessions updated: ${updatedCount}`);
      console.log(`Sessions skipped: ${skippedCount}`);
      console.log(`Sessions with child not found: ${childNotFoundCount}`);

      alert(`Success! Updated ${updatedCount} sessions with age and DOB. Skipped ${skippedCount} sessions.`);

      return {
        success: true,
        totalChildren: childrenSnapshot.docs.length,
        childrenWithDob: childrenProcessed,
        childrenWithoutDob,
        totalSessions,
        updatedCount,
        skippedCount,
        childNotFoundCount
      };

    } catch (error: any) {
      console.error('Error in bulk age addition:', error);
      alert(`Error: ${error.message || 'Failed to update sessions'}`);
      throw error;
    }
  }

  /**
   * Calculate age from date of birth (Firestore Timestamp)
   * @param dob - Firestore Timestamp or Date object
   * @returns Age in years
   */
  private calculateAgeFromDob(dob: any): number {
    const today = new Date();
    const birthDate = dob.toDate ? dob.toDate() : new Date(dob);

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    // If the birth month has not occurred yet in the current year or it is the same month but the day is not reached
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age;
  }

  ngOnInit(): void {
    this.mNotifications.bulkDeleteNotifications()
// this.mTriggerService.checkCounsellingReports()
    // void this.mLogService.CreateAdmissionLogs()
// const result =        this.mTaskService.getTaskForAndCollaboratorsEducationTrigger('ALBorivali','ALBorivali')
//         console.log('RESULT',result)
//         const startDate = new Date(2023, 9, 1); // Months are zero-based, so 9 represents October
//         const endDate = new Date(2023, 11, 31); // Months are zero-based, so 12 represents December

//     exports.childGroupSessionAttendanceCheckCallable = childGroupSessionAttendanceCheckCallable
//     exports.counsellingIndividualDocumentCheckCallable = counsellingIndividualDocumentCheckCallable
//     exports.groupSessionDocumentCheckCallable = groupSessionDocumentCheckCallable
//     exports.groupCounsellingSessionCheckCallable = groupCounsellingSessionCheckCallable
//    this.callFunction()
//     this.createDailyInventoryCount()
  }



  async addAllProductsToCentre(
    centreCode: string,
    centreName: string
  ): Promise<void> {

    try {
      const productsSnap = await this.mFirestore
        .collection(this.env.productsSubcollection)
        .ref
        .get();

      if (productsSnap.empty) return;

      // 🔥 Sort products (optional but recommended)
      const sortedDocs = productsSnap.docs.sort(
        (a, b) => Number(a.id) - Number(b.id)
      );

      const BATCH_LIMIT = 400;
      let batch = this.mFirestore.firestore.batch();
      let batchCount = 0;

      let counter = 10143; // ✅ DOC ID & SKU start from 1

      for (const doc of sortedDocs) {
        const productData: any = doc.data();

        // ✅ NEW document ID = 1,2,3...
        const newDocId = counter.toString();

        const centreProductRef = this.mFirestore.firestore
          .collection(this.env.products)
          .doc(centreCode)
          .collection(this.env.productsSubcollection)
          .doc(newDocId);

        batch.set(
          centreProductRef,
          {
            ...productData,
            sku: counter,          // ✅ same as doc ID
            productId: newDocId,   // optional
            centreCode,
            centreName,
            updatedAt: new Date()
          }
        );

        counter++;
        batchCount++;

        if (batchCount === BATCH_LIMIT) {
          await batch.commit();
          batch = this.mFirestore.firestore.batch();
          batchCount = 0;
        }
      }

      if (batchCount > 0) {
        await batch.commit();
      }

      console.log(`✅ ${counter - 1} products created with new IDs for centre ${centreCode}`);

    } catch (error) {
      console.error('❌ Error syncing products:', error);
    }
  }





  createDailyInventoryCount() {
    // Read all inventory documents and create a daily count entry for each
    this.mFirestore.collection(this.env.centreInventory).get().subscribe(
      (res) => {
        res.forEach(doc => {
          const data = (doc as any).data ? (doc as any).data() : {};
          const payload = {
            centreCode: (doc as any).id || null,
            date: this.getTodaysDate(),
            products: data['products'],
            createdAt: new Date()
          };
          // Add a simple record to ALDailyInventoryCount (non-destructive)
          this.mFirestore.collection(this.env.dailyInventoryCount).add(payload)
            .then(() => console.log('Added daily inventory count for', payload.centreCode))
            .catch(err => console.error('Error adding daily inventory count for', payload.centreCode, err));
        });
      },
      (err) => console.error('Error fetching ALInventory:', err)
    );
  }

  callFunction() {
    this.fns.httpsCallable('groupSessionDocumentCheckCallable')({})
      .subscribe({
        next: (result: any) => {
          if (result.data?.success) {
            console.log('Attendance check results:', result.data.results);
          } else {
            console.error('Function failed:', result.data?.error || 'Unknown error');
          }
        },
        error: (error: any) => {
          console.error('Callable error:', error);
        }
      });
  }


  // Function to organize the data by hospital and centre
  organizeDataByHospitalAndCentre(): any {
    const hospitalCentreData: any = {};
    this.mData.forEach(child => {
      const {hospital, centre, gender, ageGroup, type, childCode} = child;
      const key = `${hospital}_${centre}`;
      if (!hospitalCentreData[key]) {
        hospitalCentreData[key] = [];
      }
      hospitalCentreData[key].push({hospital, centre, gender, ageGroup, type, childCode});
    });
    return hospitalCentreData;
  }

  private getAgeGroup(age: number): string {
    let ageGroup = ''
    if (age != null) {
      if (age <= 3 && age > 0) {
        ageGroup = '0-3'
      } else if (age >= 4 && age <= 7) {
        ageGroup = '4-7'
      } else if (age >= 8 && age <= 10) {
        ageGroup = '8-10'
      } else if (age >= 11 && age <= 14) {
        ageGroup = '11-14'
      } else ageGroup = 'Others'

    }
    return ageGroup
  }

  private download(mData: any[]) {
    const excelData = this.mData.map(child => {
      return {
        Hospital: child.hospital,
        Centre: child.centre,
        AgeGroup: child.ageGroup,
        Type: child.type,
        Gender: child.gender
        // Add more fields as needed
      };
    });

// Create a new workbook and add the worksheet
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');

// Convert the workbook to a binary Excel file and trigger download
    const excelBuffer = XLSX.write(workbook, {bookType: 'xlsx', type: 'array'});
    const blob = new Blob([excelBuffer], {type: 'application/octet-stream'});
    const fileName = 'mData.xlsx';

// Trigger download

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

  }

  getTotalRows(hospitalData: any): number {
    let totalRows = 0;
    for (const centre in hospitalData) {
      totalRows += hospitalData[centre].length;
    }
    return totalRows;
  }

  // Function to get the unique hospitals from the data
  getUniqueHospitals(): string[] {
    return Object.keys(this.tableData);
  }

  // Utility: return today's date in dd-MM-yyyy format
  getTodaysDate(): string {
    const d = new Date();
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  }

  sampleEducationTrigger() {

    const centre = 'ALBorivali'; // Replace with actual centre
    const childName = 'Child Name'; // Replace with actual child name
    const taskDate = new Date(); // Replace with actual task date
    // Fetch assignees for the task
    const {assignees} = this.mTaskService.getTaskForAndCollaboratorsEducationTrigger(centre, 'Rapport Session');
    const mEducationTask: Partial<Task> = {
      title: `Conduct Rapport Session for ${childName}`,
      done: false,
      priority: 'medium',
      assignees: assignees,
      due_date: taskDate,
      note: `Conduct Rapport session of child ${childName} of ${centre}`,
    };

    this.mTaskService.addAutomatedTask(mEducationTask);


  }

  sampleCousnellingTrigger(centreName: string, taskDate: Date) {
    const childAge = ''
    const childName = ''
    const {assignees} = this.mTaskService.getTaskForAndCollaboratorsEducationTrigger('ALBorivali', 'Counselling Session');
    const mCounsellingTask: Partial<Task> = {
      title: `Conduct Counselling Session for ${childName}`,
      done: false,
      priority: 'medium',
      assignees: assignees,
      due_date: taskDate,
      note: `Conduct Counselling session of child ${childName} of ${centreName}`,
    };

    this.mTaskService.addAutomatedTask(mCounsellingTask);
  }


  onSubmit() {
    console.log('Calling Firebase Callable Function...');
    // Get a reference to the callable function
    const callableFunction = this.fns.httpsCallable('createCounsellingGroupTask');

    // Call the function with some data
    callableFunction({name: 'Gaurav', source: 'Angular App'}).subscribe({
      next: (result) => {
        console.log('Callable Function Success:', result);
        // Handle the successful response from the function
      },
      error: (error) => {
        console.error('Callable Function Error:', error);
        // Handle any errors that occur during the function call
      },
      complete: () => {
        console.log('Callable Function call completed.');
      }
    });
  }

  /**
   * Change user email using adminChangeUserEmail callable function
   * @param uid - The user ID whose email needs to be changed
   * @param newEmail - The new email address
   */
  changeUserEmail(uid: string, newEmail: string) {
    console.log('Calling adminChangeUserEmail function...');

    // Get a reference to the callable function
    const callableFunction = this.fns.httpsCallable('adminChangeUserEmail');

    // Call the function with uid and newEmail
    callableFunction({ uid, newEmail }).subscribe({
      next: (result: any) => {
        console.log('Email change success:', result);
        alert(`Success: ${result.message || 'Email updated successfully'}`);
      },
      error: (error: any) => {
        console.error('Email change error:', error);
        alert(`Error: ${error.message || 'Failed to update email'}`);
      },
      complete: () => {
        console.log('Email change function call completed.');
      }
    });
  }

  /**
   * Change user email using adminChangeUserEmail callable function (Modular API version)
   * @param uid - The user ID whose email needs to be changed
   * @param newEmail - The new email address
   */
  async changeUserEmailModular(uid: string, newEmail: string) {
    console.log('Calling adminChangeUserEmail function (modular)...');

    try {
      // Get a reference to the callable function using modular API
      const callableFunction = httpsCallable(this.functions, 'adminChangeUserEmail');

      // Call the function with uid and newEmail
      const result = await callableFunction({ uid, newEmail });

      console.log('Email change success:', result);
      alert(`Success: ${(result.data as any)?.message || 'Email updated successfully'}`);
    } catch (error: any) {
      console.error('Email change error:', error);
      alert(`Error: ${error.message || 'Failed to update email'}`);
    }
  }

  /**
   * Copy entire Firestore collection to another collection
   * @param sourceCollection - Source collection path
   * @param targetCollection - Target collection path
   * @param batchSize - Number of documents to process per batch (default: 500)
   * @param preserveDocIds - Whether to preserve original document IDs (default: true)
   */
  async copyCollection(
    sourceCollection: string,
    targetCollection: string,
    batchSize: number = 500,
    preserveDocIds: boolean = true
  ) {
    console.log(`Starting copy from ${sourceCollection} to ${targetCollection}...`);

    try {
      // Get all documents from source collection
      const snapshot = await this.mFirestore.collection(sourceCollection).get().toPromise();

      if (!snapshot || snapshot.empty) {
        console.warn(`Source collection ${sourceCollection} is empty or doesn't exist.`);
        alert(`No documents found in ${sourceCollection}`);
        return;
      }

      const totalDocs = snapshot.docs.length;
      console.log(`Found ${totalDocs} documents to copy.`);

      let copiedCount = 0;
      let batch = this.mFirestore.firestore.batch();
      let batchCount = 0;

      for (const doc of snapshot.docs) {
        const data = doc.data() as any;
        const targetRef = preserveDocIds
          ? this.mFirestore.collection(targetCollection).doc(doc.id).ref
          : this.mFirestore.collection(targetCollection).doc().ref;

        // Add metadata
        const payload = {
          ...data,
          _copiedFrom: sourceCollection,
          _copiedAt: new Date(),
          _originalDocId: doc.id
        };

        batch.set(targetRef, payload);
        batchCount++;

        // Commit batch when reaching batch size or last document
        if (batchCount >= batchSize || copiedCount + batchCount >= totalDocs) {
          await batch.commit();
          copiedCount += batchCount;
          console.log(`Progress: ${copiedCount}/${totalDocs} documents copied`);

          // Reset batch for next iteration
          batch = this.mFirestore.firestore.batch();
          batchCount = 0;
        }
      }

      console.log(`✅ Successfully copied ${copiedCount} documents from ${sourceCollection} to ${targetCollection}`);
      alert(`Success! Copied ${copiedCount} documents to ${targetCollection}`);

      return {
        success: true,
        totalCopied: copiedCount,
        sourceCollection,
        targetCollection
      };

    } catch (error: any) {
      console.error('Error copying collection:', error);
      alert(`Error: ${error.message || 'Failed to copy collection'}`);
      throw error;
    }
  }

  /**
   * Copy collection without batch processing (one document at a time)
   * Useful for debugging or when you need individual document tracking
   * @param sourceCollection - Source collection path
   * @param targetCollection - Target collection path
   */
  async copyCollectionSequential(
    sourceCollection: string,
    targetCollection: string
  ) {
    console.log(`Starting sequential copy from ${sourceCollection} to ${targetCollection}...`);

    try {
      const snapshot = await this.mFirestore.collection(sourceCollection).get().toPromise();

      if (!snapshot || snapshot.empty) {
        console.warn(`Source collection ${sourceCollection} is empty.`);
        alert(`No documents found in ${sourceCollection}`);
        return;
      }

      const totalDocs = snapshot.docs.length;
      let copiedCount = 0;

      for (const doc of snapshot.docs) {
        const data = doc.data() as any;
        const targetDocRef = this.mFirestore.collection(targetCollection).doc(doc.id);

        // Copy document
        await targetDocRef.set({
          ...data,
          _copiedFrom: sourceCollection,
          _copiedAt: new Date()
        });
        copiedCount++;

        console.log(`Copied document ${doc.id} (${copiedCount}/${totalDocs})`);
      }

      console.log(`✅ Sequential copy completed: ${copiedCount} documents`);
      alert(`Success! Copied ${copiedCount} documents to ${targetCollection}`);

      return {
        success: true,
        totalCopied: copiedCount,
        sourceCollection,
        targetCollection
      };

    } catch (error: any) {
      console.error('Error in sequential copy:', error);
      alert(`Error: ${error.message || 'Failed to copy collection'}`);
      throw error;
    }
  }

  /**
   * Download a single Firestore document as JSON file
   * @param collectionPath - Collection path (e.g., 'ALInventory')
   * @param documentId - Document ID to download
   */
  async downloadDocumentAsJson(collectionPath: string, documentId: string) {
    console.log(`Downloading document ${documentId} from ${collectionPath}...`);

    try {
      const docRef = this.mFirestore.collection(collectionPath).doc(documentId);
      const docSnapshot = await docRef.get().toPromise();

      if (!docSnapshot || !docSnapshot.exists) {
        console.warn(`Document ${documentId} not found in ${collectionPath}`);
        alert(`Document not found: ${collectionPath}/${documentId}`);
        return;
      }

      const data = docSnapshot.data() as any;

      // Convert to JSON string with pretty formatting (only data, no metadata)
      const jsonString = JSON.stringify(data, null, 2);

      // Create blob and download
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');

      // Create filename: collectionPath_documentId_timestamp.json
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').split('T')[0];
      const filename = `${collectionPath}_${documentId}_${timestamp}.json`;

      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      console.log(`✅ Document downloaded as ${filename}`);
      alert(`Document downloaded successfully as ${filename}`);

    } catch (error: any) {
      console.error('Error downloading document:', error);
      alert(`Error: ${error.message || 'Failed to download document'}`);
      throw error;
    }
  }

  /**
   * Download a specific field from a Firestore document as JSON file
   * Example: Download only 'products' field from ALInventory/ALBorivali
   * @param collectionPath - Collection path (e.g., 'ALInventory')
   * @param documentId - Document ID (e.g., 'ALBorivali')
   * @param fieldPath - Field path to download (e.g., 'products')
   */
  async downloadDocumentFieldAsJson(collectionPath: string, documentId: string, fieldPath: string) {
    console.log(`Downloading field '${fieldPath}' from ${collectionPath}/${documentId}...`);

    try {
      const docRef = this.mFirestore.collection(collectionPath).doc(documentId);
      const docSnapshot = await docRef.get().toPromise();

      if (!docSnapshot || !docSnapshot.exists) {
        console.warn(`Document ${documentId} not found in ${collectionPath}`);
        alert(`Document not found: ${collectionPath}/${documentId}`);
        return;
      }

      const data = docSnapshot.data() as any;

      // Extract the specific field
      const fieldData = data?.[fieldPath];

      if (!fieldData) {
        console.warn(`Field '${fieldPath}' not found in document`);
        alert(`Field '${fieldPath}' not found in ${collectionPath}/${documentId}`);
        return;
      }

      // Convert to JSON string with pretty formatting
      const jsonString = JSON.stringify(fieldData, null, 2);

      // Create blob and download
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');

      // Create filename: collectionPath_documentId_fieldPath_timestamp.json
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').split('T')[0];
      const filename = `${collectionPath}_${documentId}_${fieldPath}_${timestamp}.json`;

      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      console.log(`✅ Field '${fieldPath}' downloaded as ${filename}`);
      alert(`Field '${fieldPath}' downloaded successfully as ${filename}`);

    } catch (error: any) {
      console.error('Error downloading document field:', error);
      alert(`Error: ${error.message || 'Failed to download document field'}`);
      throw error;
    }
  }

  /**
   * Download entire Firestore collection as JSON file
   * @param collectionPath - Collection path to download
   */
  async downloadCollectionAsJson(collectionPath: string) {
    console.log(`Downloading collection ${collectionPath}...`);

    try {
      const snapshot = await this.mFirestore.collection(collectionPath).get().toPromise();

      if (!snapshot || snapshot.empty) {
        console.warn(`Collection ${collectionPath} is empty or doesn't exist.`);
        alert(`No documents found in ${collectionPath}`);
        return;
      }

      // Build collection data object - only document data keyed by ID
      const collectionData: any = {};

      snapshot.docs.forEach(doc => {
        collectionData[doc.id] = doc.data();
      });

      // Convert to JSON string with pretty formatting (no metadata wrapper)
      const jsonString = JSON.stringify(collectionData, null, 2);

      // Create blob and download
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');

      // Create filename: collectionPath_timestamp.json
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').split('T')[0];
      const filename = `${collectionPath}_${timestamp}.json`;

      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      console.log(`✅ Collection downloaded as ${filename} (${snapshot.docs.length} documents)`);
      alert(`Collection downloaded successfully: ${snapshot.docs.length} documents saved to ${filename}`);

      return {
        success: true,
        filename: filename,
        documentCount: snapshot.docs.length
      };

    } catch (error: any) {
      console.error('Error downloading collection:', error);
      alert(`Error: ${error.message || 'Failed to download collection'}`);
      throw error;
    }
  }

  /**
   * Upload JSON file data to a Firestore document
   * @param collectionPath - Collection path (e.g., 'ALInventory')
   * @param documentId - Document ID (e.g., 'ALBorivali')
   * @param file - JSON file to upload
   * @param mergeData - Whether to merge with existing data or overwrite (default: false = overwrite)
   */
  async uploadJsonToDocument(collectionPath: string, documentId: string, file: File, mergeData: boolean = false) {
    console.log(`Uploading JSON file to ${collectionPath}/${documentId}...`);

    try {
      // Read file content
      const fileContent = await this.readFileAsText(file);

      // Parse JSON
      let jsonData: any;
      try {
        jsonData = JSON.parse(fileContent);
      } catch (parseError) {
        console.error('Invalid JSON file:', parseError);
        alert('Error: Invalid JSON file format');
        return;
      }

      // Upload to Firestore
      const docRef = this.mFirestore.collection(collectionPath).doc(documentId);

      if (mergeData) {
        await docRef.set(jsonData, { merge: true });
        console.log(`✅ Data merged into ${collectionPath}/${documentId}`);
        alert(`Data successfully merged into ${collectionPath}/${documentId}`);
      } else {
        await docRef.set(jsonData);
        console.log(`✅ Data uploaded to ${collectionPath}/${documentId}`);
        alert(`Data successfully uploaded to ${collectionPath}/${documentId}`);
      }

      return {
        success: true,
        collectionPath,
        documentId,
        operation: mergeData ? 'merge' : 'overwrite'
      };

    } catch (error: any) {
      console.error('Error uploading JSON to document:', error);
      alert(`Error: ${error.message || 'Failed to upload JSON to document'}`);
      throw error;
    }
  }

  /**
   * Upload JSON file to create/update multiple documents in a collection
   * JSON format should be: { "docId1": {...data}, "docId2": {...data} }
   * @param collectionPath - Collection path
   * @param file - JSON file containing multiple documents
   * @param mergeData - Whether to merge with existing data or overwrite
   */
  async uploadJsonToCollection(collectionPath: string, file: File, mergeData: boolean = false) {
    console.log(`Uploading JSON file to collection ${collectionPath}...`);

    try {
      // Read file content
      const fileContent = await this.readFileAsText(file);

      // Parse JSON
      let jsonData: any;
      try {
        jsonData = JSON.parse(fileContent);
      } catch (parseError) {
        console.error('Invalid JSON file:', parseError);
        alert('Error: Invalid JSON file format');
        return;
      }

      // Validate JSON structure (should be an object with document IDs as keys)
      if (typeof jsonData !== 'object' || Array.isArray(jsonData)) {
        alert('Error: JSON should be an object with document IDs as keys');
        return;
      }

      const documentIds = Object.keys(jsonData);
      if (documentIds.length === 0) {
        alert('Error: No documents found in JSON file');
        return;
      }

      // Upload documents in batches
      let batch = this.mFirestore.firestore.batch();
      let batchCount = 0;
      let uploadedCount = 0;
      const batchSize = 500;

      for (const docId of documentIds) {
        const docRef = this.mFirestore.collection(collectionPath).doc(docId).ref;
        const docData = jsonData[docId];

        if (mergeData) {
          batch.set(docRef, docData, { merge: true });
        } else {
          batch.set(docRef, docData);
        }
        batchCount++;

        // Commit batch when reaching batch size
        if (batchCount >= batchSize) {
          await batch.commit();
          uploadedCount += batchCount;
          console.log(`Progress: ${uploadedCount}/${documentIds.length} documents uploaded`);
          batch = this.mFirestore.firestore.batch();
          batchCount = 0;
        }
      }

      // Commit remaining documents
      if (batchCount > 0) {
        await batch.commit();
        uploadedCount += batchCount;
      }

      console.log(`✅ Successfully uploaded ${uploadedCount} documents to ${collectionPath}`);
      alert(`Success! Uploaded ${uploadedCount} documents to ${collectionPath}`);

      return {
        success: true,
        collectionPath,
        documentCount: uploadedCount,
        operation: mergeData ? 'merge' : 'overwrite'
      };

    } catch (error: any) {
      console.error('Error uploading JSON to collection:', error);
      alert(`Error: ${error.message || 'Failed to upload JSON to collection'}`);
      throw error;
    }
  }

  /**
   * Helper method to read file as text
   * @param file - File to read
   */
  private readFileAsText(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: any) => resolve(e.target.result);
      reader.onerror = (e) => reject(e);
      reader.readAsText(file);
    });
  }

  /**
   * Trigger file picker for JSON upload to document
   * @param collectionPath - Collection path
   * @param documentId - Document ID
   * @param mergeData - Whether to merge or overwrite
   */
  triggerJsonUploadForDocument(collectionPath: string, documentId: string, mergeData: boolean = false) {
    if (!collectionPath || !documentId) {
      alert('Please enter both Collection Path and Document ID');
      return;
    }

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e: any) => {
      const file = e.target.files[0];
      if (file) {
        await this.uploadJsonToDocument(collectionPath, documentId, file, mergeData);
      }
    };
    input.click();
  }

  /**
   * Trigger file picker for JSON upload to collection
   * @param collectionPath - Collection path
   * @param mergeData - Whether to merge or overwrite
   */
  triggerJsonUploadForCollection(collectionPath: string, mergeData: boolean = false) {
    if (!collectionPath) {
      alert('Please enter Collection Path');
      return;
    }

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e: any) => {
      const file = e.target.files[0];
      if (file) {
        await this.uploadJsonToCollection(collectionPath, file, mergeData);
      }
    };
    input.click();
  }

  /**
   * Copy document from ALInventoryB to ALInventory with SKU correction
   * This method:
   * 1. Fetches the document from ALInventoryB
   * 2. Gets all products from ALProducts collection
   * 3. Matches products by name and updates SKUs
   * 4. Creates corrected products map with matching SKU as key and in data
   * 5. Saves to ALInventory collection
   *
   * @param documentId - Document ID to copy (e.g., 'ALBorivali')
   */
  async copyAndFixInventoryDocument(documentId: string) {
    console.log(`Starting copy and fix for document: ${documentId}`);

    try {
      // Step 1: Fetch document from ALInventoryB
      const sourceDocRef = this.mFirestore.collection(this.env.inventoryNew).doc(documentId);
      const sourceSnapshot = await sourceDocRef.get().toPromise();

      if (!sourceSnapshot || !sourceSnapshot.exists) {
        console.error(`Document ${documentId} not found in ALInventoryNew`);
        alert(`Document ${documentId} not found in ALInventoryNew`);
        return;
      }

      const sourceData = sourceSnapshot.data() as any;
      const sourceProducts = sourceData?.products || {};

      if (Object.keys(sourceProducts).length === 0) {
        console.warn('No products found in source document');
        alert('No products found in source document');
        return;
      }

      console.log(`Found ${Object.keys(sourceProducts).length} products in source document`);

      // Step 2: Fetch all products from ALProductsByCentre collection
      const alProductsSnapshot = await this.mFirestore.collection(`${this.env.products}/${documentId}/${this.env.productsSubcollection}`).get().toPromise();

      if (!alProductsSnapshot || alProductsSnapshot.empty) {
        console.error('ALProducts collection is empty or not found');
        alert('ALProducts collection is empty. Cannot proceed with SKU correction.');
        return;
      }

      // Build a lookup map: productName -> correct SKU (docId)
      const productNameToSku = new Map<string, string>();
      alProductsSnapshot.docs.forEach(doc => {
        const productData = doc.data() as any;
        const productName = (productData?.productName || '').toString().trim().toLowerCase();
        const correctSku = doc.id; // Document ID is the correct SKU

        if (productName) {
          productNameToSku.set(productName, correctSku);
        }
      });

      console.log(`Loaded ${productNameToSku.size} products from ALProducts for matching`);

      // Step 3: Process products and fix SKUs
      const correctedProducts: any = {};
      let correctedCount = 0;
      let notFoundCount = 0;
      const notFoundProducts: string[] = [];

      Object.entries(sourceProducts).forEach(([oldKey, productData]: [string, any]) => {
        const productName = (productData?.productName || '').toString().trim().toLowerCase();

        if (!productName) {
          console.warn(`Product with key ${oldKey} has no productName, skipping`);
          return;
        }

        // Look up correct SKU from ALProducts
        const correctSku = productNameToSku.get(productName);

        if (correctSku) {
          // Update product data with correct sku (lowercase only)
          const correctedProduct = {
            ...productData,
            sku: correctSku
          };

          // Remove any uppercase SKU field if it exists
          delete correctedProduct.SKU;

          // Add tracking metadata
          if (oldKey !== correctSku) {
            correctedProduct._correctedFrom = oldKey;
            correctedProduct._correctedAt = new Date().toISOString();
          }

          // Use correct sku as the map key (key === sku)
          correctedProducts[correctSku] = correctedProduct;
          correctedCount++;

          if (oldKey !== correctSku) {
            console.log(`✓ Fixed: ${productData.productName} | Old Key: ${oldKey} → New Key & sku: ${correctSku}`);
          }
        } else {
          // Product not found in ALProducts - keep original but log warning
          console.warn(`⚠ Product not found in ALProducts: ${productData.productName} (key: ${oldKey})`);
          notFoundProducts.push(productData.productName);
          notFoundCount++;

          // Keep original product with warning flag and ensure key matches sku
          const originalSku = productData?.sku || productData?.SKU || oldKey;
          correctedProducts[originalSku] = {
            ...productData,
            sku: originalSku,
            _notFoundInALProducts: true
          };

          // Remove uppercase SKU field
          delete correctedProducts[originalSku].SKU;
        }
      });

      console.log(`\n=== SKU Correction Summary ===`);
      console.log(`Total products: ${Object.keys(sourceProducts).length}`);
      console.log(`Corrected: ${correctedCount}`);
      console.log(`Not found in ALProducts: ${notFoundCount}`);
      if (notFoundProducts.length > 0) {
        console.log(`Not found products:`, notFoundProducts);
      }

      // Step 4: Create new document data
      const correctedDocData = {
        ...sourceData,
        products: correctedProducts,
        _copiedFrom: 'ALInventoryB',
        _copiedAt: new Date().toISOString(),
        _skusCorrected: correctedCount,
        _productsNotFound: notFoundCount
      };

      // Step 5: Save to ALInventory collection
      const targetDocRef = this.mFirestore.collection(this.env.centreInventory).doc(documentId);
      await targetDocRef.set(correctedDocData);

      console.log(`✅ Document saved to ALInventory/${documentId}`);

      let message = `Success! Document copied and fixed.
- Total products: ${Object.keys(sourceProducts).length}
- SKUs corrected: ${correctedCount}
- Products not found in ALProducts: ${notFoundCount}`;

      if (notFoundCount > 0) {
        message += `\n\n⚠ Warning: The following ${notFoundCount} product(s) were not found in ALProducts and kept their original SKUs:\n`;
        message += notFoundProducts.map((name, index) => `${index + 1}. ${name}`).join('\n');
      }

      alert(message);

      return {
        success: true,
        documentId,
        totalProducts: Object.keys(sourceProducts).length,
        correctedCount,
        notFoundCount,
        notFoundProducts
      };

    } catch (error: any) {
      console.error('Error copying and fixing inventory document:', error);
      alert(`Error: ${error.message || 'Failed to copy and fix document'}`);
      throw error;
    }
  }

  /**
   * Update all products in ALProducts collection to ensure sku and id fields match document ID
   * This method:
   * 1. Fetches all documents from ALProducts collection
   * 2. For each product, sets sku = docId and id = docId
   * 3. Removes any uppercase SKU field
   * 4. Updates documents in batches for efficiency
   */
  async updateALProductsSKUs() {
    console.log('Starting ALProducts SKU update...');

    try {
      // Fetch all products from ALProducts collection
      const snapshot = await this.mFirestore.collection(this.env.productsSubcollection).get().toPromise();

      if (!snapshot || snapshot.empty) {
        console.warn('ALProducts collection is empty');
        alert('ALProducts collection is empty');
        return;
      }

      const totalProducts = snapshot.docs.length;
      console.log(`Found ${totalProducts} products to update`);

      let batch = this.mFirestore.firestore.batch();
      let batchCount = 0;
      let updatedCount = 0;
      const batchSize = 500;
      const updates: any[] = [];

      // Process each product
      snapshot.docs.forEach(doc => {
        const docId = doc.id;
        const currentData = doc.data() as any;

        // Check if update is needed
        const needsUpdate =
          currentData.sku !== docId ||
          currentData.id !== docId ||
          currentData.SKU !== undefined;

        if (needsUpdate) {
          // Create updated data with sku and id matching docId
          const updatedData = {
            ...currentData,
            sku: docId,
            id: docId
          };

          // Remove uppercase SKU field if it exists
          delete updatedData.SKU;

          // Add to batch
          batch.update(doc.ref, updatedData);
          batchCount++;

          updates.push({
            docId,
            oldSku: currentData.sku,
            oldId: currentData.id,
            productName: currentData.productName
          });

          console.log(`✓ Will update: ${currentData.productName || 'Unknown'} | DocID: ${docId} | Old sku: ${currentData.sku} → New sku: ${docId}`);
        }

        // Commit batch when reaching batch size
        if (batchCount >= batchSize) {
          batch.commit().then(() => {
            updatedCount += batchCount;
            console.log(`Progress: ${updatedCount}/${updates.length} products updated`);
          });
          batch = this.mFirestore.firestore.batch();
          batchCount = 0;
        }
      });

      // Commit remaining updates
      if (batchCount > 0) {
        await batch.commit();
        updatedCount += batchCount;
      }

      console.log(`\n=== ALProducts Update Summary ===`);
      console.log(`Total products in collection: ${totalProducts}`);
      console.log(`Products updated: ${updates.length}`);
      console.log(`Products already correct: ${totalProducts - updates.length}`);

      if (updates.length > 0) {
        console.log('\nUpdated products:');
        updates.forEach((update, index) => {
          console.log(`${index + 1}. ${update.productName} (${update.docId})`);
        });
      }

      const message = `Success! ALProducts collection updated.
- Total products: ${totalProducts}
- Updated: ${updates.length}
- Already correct: ${totalProducts - updates.length}

All products now have sku and id fields matching their document ID.`;

      alert(message);

      return {
        success: true,
        totalProducts,
        updatedCount: updates.length,
        alreadyCorrect: totalProducts - updates.length,
        updates
      };

    } catch (error: any) {
      console.error('Error updating ALProducts SKUs:', error);
      alert(`Error: ${error.message || 'Failed to update ALProducts SKUs'}`);
      throw error;
    }
  }

  /**
   * Update product map keys in ALInventory documents to match their sku field values
   * Fixes the issue where product keys don't match their sku values
   * Example: products["1000"].sku = "1131" becomes products["1131"].sku = "1131"
   *
   * @param documentId - Specific document to update, or empty to update all documents
   */
  async updateALInventoryProductKeys(documentId?: string) {
    console.log('Starting ALInventory product keys update...');

    try {
      let snapshot: any;

      if (documentId) {
        // Update single document
        const docRef = this.mFirestore.collection(this.env.centreInventory).doc(documentId);
        const docSnap = await docRef.get().toPromise();

        if (!docSnap || !docSnap.exists) {
          alert(`Document ${documentId} not found in ALInventory`);
          return;
        }

        snapshot = { docs: [docSnap] };
      } else {
        // Update all documents
        snapshot = await this.mFirestore.collection(this.env.centreInventory).get().toPromise();
      }

      if (!snapshot || !snapshot.docs || snapshot.docs.length === 0) {
        console.warn('No documents found in ALInventory');
        alert('No documents found in ALInventory');
        return;
      }

      const totalDocs = snapshot.docs.length;
      console.log(`Found ${totalDocs} document(s) to process`);

      let totalProductsProcessed = 0;
      let totalProductsFixed = 0;
      const documentSummaries: any[] = [];

      // Process each document i.e. centre document in inventory
      for (const doc of snapshot.docs) {
        const docId = doc.id;
        const data = doc.data() as any;
        const products = data?.products || {};

        if (Object.keys(products).length === 0) {
          console.log(`⊘ Skipping ${docId} - no products`);
          continue;
        }

        console.log(`\nProcessing ${docId}...`);
        const productsCount = Object.keys(products).length;

        // Rebuild products map with keys matching sku values
        const updatedProducts: any = {};
        let fixedInDoc = 0;
        const fixes: any[] = [];

        Object.entries(products).forEach(([oldKey, productData]: [string, any]) => {
          const sku = productData?.sku || productData?.SKU || oldKey;

          // Use sku as the new key and update id field to match sku
          updatedProducts[sku] = {
            ...productData,
            sku: sku,
            id: sku  // Ensure id matches sku
          };

          // Remove uppercase SKU field
          delete updatedProducts[sku].SKU;

          totalProductsProcessed++;

          // Track if key or id changed
          const keyChanged = oldKey !== sku;
          const idChanged = productData?.id !== sku;

          if (keyChanged || idChanged) {
            fixedInDoc++;
            totalProductsFixed++;

            const changes: string[] = [];
            if (keyChanged) changes.push(`Key: ${oldKey} → ${sku}`);
            if (idChanged) changes.push(`id: ${productData?.id || 'undefined'} → ${sku}`);

            fixes.push({
              productName: productData.productName,
              oldKey,
              newKey: sku,
              oldId: productData?.id,
              newId: sku
            });
            console.log(`  ✓ Fixed: ${productData.productName} | ${changes.join(', ')}`);
          }
        });

        // Update document if any changes were made
        if (fixedInDoc > 0) {
          const updatedData = {
            ...data,
            products: updatedProducts,
            _keysUpdated: true,
            _keysUpdatedAt: new Date().toISOString(),
            _keysFixedCount: fixedInDoc
          };

          await this.mFirestore.collection(this.env.centreInventory).doc(docId).set(updatedData);
          console.log(`  ✅ Updated ${docId}: ${fixedInDoc} product key(s) fixed`);
        } else {
          console.log(`  ✓ ${docId}: All keys already correct`);
        }

        documentSummaries.push({
          docId,
          totalProducts: productsCount,
          fixed: fixedInDoc,
          fixes
        });
      }

      console.log(`\n=== ALInventory Product Keys Update Summary ===`);
      console.log(`Documents processed: ${totalDocs}`);
      console.log(`Total products processed: ${totalProductsProcessed}`);
      console.log(`Product keys fixed: ${totalProductsFixed}`);
      console.log(`Already correct: ${totalProductsProcessed - totalProductsFixed}`);

      // Show detailed summary for each document
      documentSummaries.forEach(summary => {
        if (summary.fixed > 0) {
          console.log(`\n${summary.docId}:`);
          summary.fixes.forEach((fix: any) => {
            console.log(`  - ${fix.productName}: ${fix.oldKey} → ${fix.newKey}`);
          });
        }
      });

      let message = `Success! ALInventory product keys updated.
- Documents processed: ${totalDocs}
- Total products: ${totalProductsProcessed}
- Keys fixed: ${totalProductsFixed}
- Already correct: ${totalProductsProcessed - totalProductsFixed}`;

      if (totalProductsFixed > 0) {
        message += `\n\nAll product map keys now match their sku field values.`;
      }

      alert(message);

      return {
        success: true,
        documentsProcessed: totalDocs,
        totalProductsProcessed,
        totalProductsFixed,
        documentSummaries
      };

    } catch (error: any) {
      console.error('Error updating ALInventory product keys:', error);
      alert(`Error: ${error.message || 'Failed to update product keys'}`);
      throw error;
    }
  }

  // async updateChildName() {
  //
  //   const snapshot = await this.mFirestore
  //     .collection('AdmissionLogHistory')
  //     .ref
  //     .get(); // ✅ one-time fetch
  //
  //   for (const doc of snapshot.docs) {
  //
  //     const admissionId = doc.id;
  //     const admissionData: any = doc.data();
  //     const childCode = admissionData.childCode;
  //
  //     if (!childCode) {
  //       console.warn('No childCode for', admissionId);
  //       continue;
  //     }
  //
  //     // 🔹 get child (one-time)
  //     const childSnap = await this.mFirestore
  //       .collection('ALChild')
  //       .doc(childCode)
  //       .ref
  //       .get();
  //
  //     if (!childSnap.exists) {
  //       console.warn('Child not found:', childCode);
  //       continue;
  //     }
  //
  //     const childData: any = childSnap.data();
  //
  //     const childName =
  //       `${childData.firstName || ''} ${childData.lastName || ''}`.trim();
  //
  //     console.log(`Updating ${admissionId} → ${childName}`);
  //
  //     // 🔹 direct update
  //     await this.mFirestore
  //       .collection('AdmissionLogHistory')
  //       .doc(admissionId)
  //       .update({ childName });
  //   }
  //
  //   console.log('✅ ALL CHILD NAMES UPDATED SUCCESSFULLY');
  // }
  async updateChildName() {

    const snapshot = await this.mFirestore
      .collection(this.env.admissionLogHistory)
      .ref
      .get(); // one-time fetch

    let updated = 0;
    let skipped = 0;

    for (const doc of snapshot.docs) {

      const admissionId = doc.id;
      const admissionData: any = doc.data();

      const childCode = admissionData.childCode;
      const existingChildName = admissionData.childName;

      // ✅ SKIP if childName already exists
      if (existingChildName && existingChildName.trim().length > 0) {
        skipped++;
        continue;
      }

      // ❌ skip if no childCode
      if (!childCode) {
        console.warn('No childCode for', admissionId);
        continue;
      }

      // 🔹 get child
      const childSnap = await this.mFirestore
        .collection(this.env.child)
        .doc(childCode)
        .ref
        .get();

      if (!childSnap.exists) {
        console.warn('Child not found:', childCode);
        continue;
      }

      const childData: any = childSnap.data();

      const childName =
        `${childData.firstName || ''} ${childData.lastName || ''}`.trim();

      if (!childName) {
        console.warn('Empty childName for', childCode);
        continue;
      }

      // 🔹 update ONLY missing childName
      await this.mFirestore
        .collection(this.env.admissionLogHistory)
        .doc(admissionId)
        .update({ childName });

      updated++;
      console.log(`Updated ${admissionId} → ${childName}`);
    }

    console.log(`✅ DONE | Updated: ${updated}, Skipped: ${skipped}`);
  }

  // =====================================================================
  // 📊 ONE-TIME BACKFILL: Build educationMonthlySummary collection
  // Run this ONCE from this screen to pre-aggregate all historical sessions.
  // After this, new sessions maintain counters automatically.
  // =====================================================================
  async buildEducationMonthlySummary() {
    if (this.isBuildingSummary) return;

    const confirmed = confirm(
      '⚠️ This will read ALL educationTest + educationGroupTest docs and write summary data.\n' +
      'It is safe to re-run — it fully overwrites existing summary data.\n\nContinue?'
    );
    if (!confirmed) return;

    this.isBuildingSummary = true;
    this.summaryBuildStatus = '⏳ Starting...';
    this.summaryBuildProgress = 0;

    // In-memory accumulator: docId → summary data
    const summaryMap = new Map<string, any>();

    const toJsDate = (d: any): Date => {
      if (!d) return new Date();
      if (d?.toDate) return d.toDate();
      if (d instanceof Date) return d;
      if (d?.seconds) return new Date(d.seconds * 1000);
      return new Date(d);
    };

    const upsert = (
      teacherName: string,
      centreCode: string,
      sessionDate: any,
      fields: { individual?: number; group?: number; followUp?: number }
    ) => {
      if (!teacherName || !centreCode || !sessionDate) return;
      const date = toJsDate(sessionDate);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const yearMonth = `${year}-${String(month).padStart(2, '0')}`;
      const sanitized = teacherName.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
      const docId = `${sanitized}_${yearMonth}_${centreCode}`;

      const existing = summaryMap.get(docId) || {
        teacherName, centreCode, yearMonth, year, month,
        individualCount: 0, groupCount: 0, followUpCount: 0
      };
      if (fields.individual) existing.individualCount += fields.individual;
      if (fields.group) existing.groupCount += fields.group;
      if (fields.followUp) existing.followUpCount += fields.followUp;
      summaryMap.set(docId, existing);
    };

    try {
      // ── Step 1: Individual sessions ──
      this.summaryBuildStatus = '📊 Reading individual sessions (educationTest)...';
      let lastDoc: any = null;
      let totalIndividual = 0;

      while (true) {
        let q: any = this.mFirestore.firestore
          .collection(this.env.education)
          .orderBy('sessionDate')
          .limit(500);
        if (lastDoc) q = q.startAfter(lastDoc);

        const snap = await q.get();
        if (snap.empty) break;

        snap.docs.forEach((doc: any) => {
          const d = doc.data();
          // Only count Present individual sessions — followUp comes from EducationFollowUpAssessment
          if (d.present !== 'Present' || !d.submittedBy || !d.centreCode || !d.sessionDate) return;
          upsert(d.submittedBy, d.centreCode, d.sessionDate, { individual: 1 });
          totalIndividual++;
        });

        lastDoc = snap.docs[snap.docs.length - 1];
        this.summaryBuildStatus = `📊 Individual sessions processed: ${totalIndividual}`;
        if (snap.docs.length < 500) break;
      }

      // ── Step 2: Group sessions ──
      this.summaryBuildStatus = '📊 Reading group sessions (educationGroupTest)...';
      lastDoc = null;
      let totalGroup = 0;

      while (true) {
        let q: any = this.mFirestore.firestore
          .collection(this.env.educationGroup)
          .orderBy('sessionDate')
          .limit(500);
        if (lastDoc) q = q.startAfter(lastDoc);

        const snap = await q.get();
        if (snap.empty) break;

        snap.docs.forEach((doc: any) => {
          const d = doc.data();
          if (!d.submittedBy || !d.sessionDate) return;
          const presentChildren = Array.isArray(d.children)
            ? d.children.filter((c: any) => c.status === 'Present')
            : [];
          if (presentChildren.length === 0) return;

          // If countType is 'bychildren', count = number of present children; otherwise count as 1 session
          const groupCount = d.countType === 'bychildren' ? d.children.length : 1;

          // Group sessions store centre in centres[] array OR root centreCode (newer sessions)
          const centreCodes: string[] = [];
          if (d.centreCode) {
            centreCodes.push(d.centreCode);
          } else if (Array.isArray(d.centres) && d.centres.length > 0) {
            d.centres.forEach((c: any) => {
              if (c?.centreCode) centreCodes.push(c.centreCode);
            });
          }

          if (centreCodes.length === 0) {
            // Last resort: count under 'All' so it's queryable
            upsert(d.submittedBy, 'All', d.sessionDate, { group: groupCount });
            totalGroup += groupCount;
            return;
          }

          // Create one summary entry per centre the session covers
          centreCodes.forEach(cc => {
            upsert(d.submittedBy, cc, d.sessionDate, { group: groupCount });
          });
          totalGroup += groupCount;
        });

        lastDoc = snap.docs[snap.docs.length - 1];
        this.summaryBuildStatus = `📊 Group sessions processed: ${totalGroup}`;
        if (snap.docs.length < 500) break;
      }

      // ── Step 3: Follow-up assessments (EducationFollowUpAssessment) ──
      // Uses fields: submittedBy, centreCode, date (Timestamp)
      this.summaryBuildStatus = '📊 Reading follow-up assessments (EducationFollowUpAssessment)...';
      lastDoc = null;
      let totalFollowUp = 0;

      while (true) {
        let q: any = this.mFirestore.firestore
          .collection(this.env.educationFollowUp)
          .orderBy('date')
          .limit(500);
        if (lastDoc) q = q.startAfter(lastDoc);

        const snap = await q.get();
        if (snap.empty) break;

        snap.docs.forEach((doc: any) => {
          const d = doc.data();
          if (!d.submittedBy || !d.centreCode || !d.date) return;
          upsert(d.submittedBy, d.centreCode, d.date, { followUp: 1 });
          totalFollowUp++;
        });

        lastDoc = snap.docs[snap.docs.length - 1];
        this.summaryBuildStatus = `📊 Follow-up assessments processed: ${totalFollowUp}`;
        if (snap.docs.length < 500) break;
      }

      // ── Step 4: Write summary docs to Firestore ──
      const entries = Array.from(summaryMap.entries());
      this.summaryBuildStatus = `💾 Writing ${entries.length} summary docs to Firestore...`;
      console.log(`📊 Total summary docs to write: ${entries.length}`);

      const BATCH_SIZE = 400;
      for (let i = 0; i < entries.length; i += BATCH_SIZE) {
        const batch = this.mFirestore.firestore.batch();
        const chunk = entries.slice(i, i + BATCH_SIZE);
        chunk.forEach(([docId, data]) => {
          const ref = this.mFirestore.firestore
            .collection(this.env.educationMonthlySummary)
            .doc(docId);
          batch.set(ref, data); // Overwrite fully — safe to re-run
        });
        await batch.commit();
        this.summaryBuildProgress = Math.round(((i + chunk.length) / entries.length) * 100);
        this.summaryBuildStatus = `💾 Written ${Math.min(i + BATCH_SIZE, entries.length)} / ${entries.length} summary docs (${this.summaryBuildProgress}%)`;
        console.log(this.summaryBuildStatus);
      }

      this.summaryBuildStatus =
        `✅ Done! Created ${entries.length} summary documents.\n` +
        `Individual: ${totalIndividual} | Group: ${totalGroup} | Follow-Up Assessments: ${totalFollowUp}`;
      this.summaryBuildProgress = 100;
      console.log('✅ buildEducationMonthlySummary complete');

    } catch (error: any) {
      this.summaryBuildStatus = `❌ Error: ${error.message}`;
      console.error('❌ buildEducationMonthlySummary failed:', error);
    } finally {
      this.isBuildingSummary = false;
    }
  }

  async addDateToCounsellingDocuments() {
    console.log('Starting Add Date & Centre Fields to Counselling Individual Documents...');
    try {
      const sessionsSnap = await this.mFirestore.collection(`${this.env.counseling}/allSessions/sessions`).get().toPromise();
      const docsSnap = await this.mFirestore.collection(`${this.env.documents}/Counselling/individualSessions`).get().toPromise();

      if (!sessionsSnap || !docsSnap) return;

      const sessionMeta = new Map<string, any>();
      sessionsSnap.docs.forEach(d => {
        const data = d.data() as any;
        if (data.date) sessionMeta.set(d.id, {
          date: data.date,
          centreCode: data.centreCode || '',
          centreName: data.centreName || '',
        });
      });

      let batch = this.mFirestore.firestore.batch();
      let count = 0;

      for (const doc of docsSnap.docs) {
        const docData = doc.data() as any;
        const meta = sessionMeta.get(doc.id);

        if (meta && docData.documents) {
          const updatedDocs = docData.documents.map((file: any) => ({
            ...file,
            date: file.date || meta.date,
            dateAdded: file.dateAdded || meta.date
          }));

          batch.update(doc.ref, {
            documents: updatedDocs,
            date: docData.date || meta.date,
            dateAdded: docData.dateAdded || meta.date,
            centreCode: docData.centreCode || meta.centreCode,
            centreName: docData.centreName || meta.centreName,
          });
          count++;
          if (count % 400 === 0) {
            await batch.commit();
            batch = this.mFirestore.firestore.batch();
          }
        }
      }
      await batch.commit();
      alert(`Updated ${count} counselling individual document records with date & centre info.`);
    } catch (e) {
      console.error(e);
    }
  }

  async addDateToGroupSessionDocuments() {
    console.log('Starting Add Date & Centre Fields to Counselling Group Session Documents...');
    try {
      const sessionsSnap = await this.mFirestore.collection(`${this.env.counseling}/allSessions/groupSessions`).get().toPromise();
      const docsSnap = await this.mFirestore.collection(`${this.env.documents}/Counselling/groupSessions`).get().toPromise();

      if (!sessionsSnap || !docsSnap) return;

      const sessionMeta = new Map<string, any>();
      sessionsSnap.docs.forEach(d => {
        const data = d.data() as any;
        if (data.date) {
          const centres: any[] = data.centres || [];
          const centreCode = centres.map((c: any) => c.centreCode).filter(Boolean).join(', ') || data.centreCode || '';
          const centreName = centres.map((c: any) => c.centreName).filter(Boolean).join(', ') || data.centreName || '';
          sessionMeta.set(d.id, { date: data.date, centreCode, centreName, centres });
        }
      });

      let batch = this.mFirestore.firestore.batch();
      let count = 0;

      for (const doc of docsSnap.docs) {
        const docData = doc.data() as any;
        const meta = sessionMeta.get(doc.id);

        if (meta && docData.documents) {
          const updatedDocs = docData.documents.map((file: any) => ({
            ...file,
            date: file.date || meta.date,
            dateAdded: file.dateAdded || meta.date
          }));

          batch.update(doc.ref, {
            documents: updatedDocs,
            date: docData.date || meta.date,
            dateAdded: docData.dateAdded || meta.date,
            centreCode: docData.centreCode || meta.centreCode,
            centreName: docData.centreName || meta.centreName,
            centres: docData.centres || meta.centres,
          });
          count++;
          if (count % 400 === 0) {
            await batch.commit();
            batch = this.mFirestore.firestore.batch();
          }
        }
      }
      await batch.commit();
      alert(`Updated ${count} counselling group session document records with date & centre info.`);
    } catch (e) {
      console.error(e);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Backfill: Add centreCode/centreName to ALDocuments/Counselling/individualSessions
  // (idempotent — only fills in missing centreCode/centreName)
  // ─────────────────────────────────────────────────────────────────────────
  async addCentreInfoToGroupDocuments() {
    console.log('Starting Add Centre Info to Counselling Individual Documents...');
    try {
      const sessionsSnap = await this.mFirestore.collection(`${this.env.counseling}/allSessions/sessions`).get().toPromise();
      const docsSnap = await this.mFirestore.collection(`${this.env.documents}/Counselling/individualSessions`).get().toPromise();

      if (!sessionsSnap || !docsSnap) return;

      const sessionMeta = new Map<string, any>();
      sessionsSnap.docs.forEach(d => {
        const data = d.data() as any;
        sessionMeta.set(d.id, {
          centreCode: data.centreCode || '',
          centreName: data.centreName || '',
        });
      });

      let batch = this.mFirestore.firestore.batch();
      let count = 0;

      for (const doc of docsSnap.docs) {
        const docData = doc.data() as any;
        const meta = sessionMeta.get(doc.id);
        if (meta && (!docData.centreCode || !docData.centreName)) {
          batch.update(doc.ref, {
            centreCode: docData.centreCode || meta.centreCode,
            centreName: docData.centreName || meta.centreName,
          });
          count++;
          if (count % 400 === 0) {
            await batch.commit();
            batch = this.mFirestore.firestore.batch();
          }
        }
      }
      await batch.commit();
      alert(`Added centre info to ${count} counselling individual document records.`);
    } catch (e) {
      console.error(e);
    }
  }

  async addDateToEducationDocuments() {
    console.log('Starting Add Date & Centre Info to Education Documents...');
    try {
      const sessionsSnap = await this.mFirestore.collection(this.env.education).get().toPromise();
      const docsSnap = await this.mFirestore.collection(`${this.env.documents}/Education/individualSessions`).get().toPromise();

      if (!sessionsSnap || !docsSnap) return;

      const sessionMeta = new Map();
      sessionsSnap.docs.forEach(d => {
        const data = d.data() as any;
        if (data.sessionDate) {
          sessionMeta.set(d.id, {
            date: data.sessionDate,
            centreCode: data.centreCode,
            centreName: data.centreName
          });
        }
      });

      let batch = this.mFirestore.firestore.batch();
      let count = 0;

      for (const doc of docsSnap.docs) {
        const docData = doc.data() as any;
        const meta = sessionMeta.get(doc.id);

        if (meta && docData.documents) {
          const updatedDocs = docData.documents.map((file: any) => ({
            ...file,
            date: file.date || meta.date,
            dateAdded: file.dateAdded || meta.date
          }));

          batch.update(doc.ref, {
            documents: updatedDocs,
            date: docData.date || meta.date,
            dateAdded: docData.dateAdded || meta.date,
            centreCode: docData.centreCode || meta.centreCode,
            centreName: docData.centreName || meta.centreName
          });
          count++;
          if (count % 400 === 0) {
            await batch.commit();
            batch = this.mFirestore.firestore.batch();
          }
        }
      }
      await batch.commit();
      alert(`Updated ${count} education individual document records.`);
    } catch (e) {
      console.error(e);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Backfill: Add date, centreCode, centreName to ALDocuments/Education/groupSessions
  // Reads from educationGroupTest (production) + educationGroupTesting (dev)
  // ─────────────────────────────────────────────────────────────────────────
  isBackfillingEduGroupDocs = false;
  eduGroupDocBackfillStatus = '';

  async addDateAndCentreInfoToEducationGroupDocuments() {
    if (this.isBackfillingEduGroupDocs) return;
    if (!confirm(
      '⚠️ This will read ALL educationGroupTest sessions and update\n' +
      'ALDocuments/Education/groupSessions with date, centreCode, centreName, centres.\n\n' +
      'Safe to re-run (idempotent — only fills in missing fields).\n\nContinue?'
    )) return;

    this.isBackfillingEduGroupDocs = true;
    this.eduGroupDocBackfillStatus = '⏳ Fetching education group sessions…';

    try {
      const sessionsSnap = await this.mFirestore.collection(this.env.educationGroup).get().toPromise();
      const docsSnap = await this.mFirestore.collection(`${this.env.documents}/Education/groupSessions`).get().toPromise();

      if (!sessionsSnap || !docsSnap) {
        this.eduGroupDocBackfillStatus = '❌ Could not fetch collections.';
        return;
      }

      this.eduGroupDocBackfillStatus = `📋 Building metadata map from ${sessionsSnap.docs.length} sessions…`;

      const sessionMeta = new Map<string, any>();
      sessionsSnap.docs.forEach(d => {
        const data = d.data() as any;
        const sessionDate = data.sessionDate || data.date || null;
        if (sessionDate) {
          const centres: any[] = data.centres || [];
          const centreCode = centres.map((c: any) => c.centreCode).filter(Boolean).join(', ') || data.centreCode || '';
          const centreName = centres.map((c: any) => c.centreName).filter(Boolean).join(', ') || data.centreName || '';
          sessionMeta.set(d.id, { date: sessionDate, centreCode, centreName, centres });
        }
      });

      this.eduGroupDocBackfillStatus = `🔄 Updating ${docsSnap.docs.length} document records…`;

      let batch = this.mFirestore.firestore.batch();
      let count = 0;
      let skipped = 0;

      for (const doc of docsSnap.docs) {
        const docData = doc.data() as any;
        const meta = sessionMeta.get(doc.id);

        if (!meta) { skipped++; continue; }
        if (!docData.documents) { skipped++; continue; }

        const updatedDocs = docData.documents.map((file: any) => ({
          ...file,
          date: file.date || meta.date,
          dateAdded: file.dateAdded || meta.date,
        }));

        batch.update(doc.ref, {
          documents: updatedDocs,
          date: docData.date || meta.date,
          dateAdded: docData.dateAdded || meta.date,
          centreCode: docData.centreCode || meta.centreCode,
          centreName: docData.centreName || meta.centreName,
          centres: docData.centres || meta.centres,
        });
        count++;

        if (count % 400 === 0) {
          await batch.commit();
          batch = this.mFirestore.firestore.batch();
          this.eduGroupDocBackfillStatus = `🔄 Committed ${count} records so far…`;
        }
      }

      await batch.commit();
      this.eduGroupDocBackfillStatus = `✅ Done! Updated ${count} records, skipped ${skipped}.`;
      alert(`Education group documents backfill complete!\nUpdated: ${count}\nSkipped (no matching session): ${skipped}`);
    } catch (e: any) {
      this.eduGroupDocBackfillStatus = `❌ Error: ${e?.message ?? e}`;
      console.error('addDateAndCentreInfoToEducationGroupDocuments failed:', e);
    } finally {
      this.isBackfillingEduGroupDocs = false;
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Backfill: Set isHighlight = true on EducationFollowUpAssessment documents
  // where the question "Any highlighted points to be shared" has ans == "Yes"
  // ─────────────────────────────────────────────────────────────────────────
  isBackfillingHighlight = false;
  highlightBackfillStatus = '';

  async backfillFollowUpHighlight() {
    if (!confirm('This will update isHighlight=true on all FollowUp Assessment docs where "Any highlighted points to be shared" answer is "Yes". Proceed?')) {
      return;
    }

    this.isBackfillingHighlight = true;
    this.highlightBackfillStatus = '🔍 Fetching EducationFollowUpAssessment documents...';

    try {
      let lastDoc: any = null;
      let totalProcessed = 0;
      let totalUpdated = 0;

      while (true) {
        let query: any = this.mFirestore.firestore
          .collection(this.env.educationFollowUp)
          .orderBy('date')
          .limit(500);

        if (lastDoc) query = query.startAfter(lastDoc);

        const snap = await query.get();
        if (snap.empty) break;

        let batch = this.mFirestore.firestore.batch();
        let batchCount = 0;

        for (const doc of snap.docs) {
          const data = doc.data() as any;
          totalProcessed++;

          // Skip if already highlighted
          if (data.isHighlight === true) continue;

          const questions: any[] = data.questions || [];

          // Find the "Any highlighted points to be shared" question
          const highlightQuestion = questions.find(
            (q: any) => q.question?.trim() === 'Any highlighted points to be shared'
          );

          if (highlightQuestion && highlightQuestion.ans?.trim() === 'Yes') {
            batch.update(doc.ref, { isHighlight: true });
            batchCount++;
            totalUpdated++;

            // Commit every 400 updates
            if (batchCount % 400 === 0) {
              await batch.commit();
              batch = this.mFirestore.firestore.batch();
              batchCount = 0;
            }
          }
        }

        // Commit remaining in batch
        if (batchCount > 0) {
          await batch.commit();
        }

        lastDoc = snap.docs[snap.docs.length - 1];
        this.highlightBackfillStatus = `📋 Processed: ${totalProcessed} | Updated: ${totalUpdated}`;

        if (snap.docs.length < 500) break;
      }

      this.highlightBackfillStatus = `✅ Done! Processed ${totalProcessed} docs. Set isHighlight=true on ${totalUpdated} records.`;
      alert(this.highlightBackfillStatus);

    } catch (e: any) {
      this.highlightBackfillStatus = `❌ Error: ${e.message}`;
      console.error('backfillFollowUpHighlight error:', e);
    } finally {
      this.isBackfillingHighlight = false;
    }
  }

  // =====================================================================
  // 📊 ONE-TIME BACKFILL: Build counsellingMonthlySummary collection
  // Run this ONCE to pre-aggregate all historical counselling sessions.
  // After this, new sessions maintain counters automatically.
  // =====================================================================
  async buildCounsellingMonthlySummary() {
    if (this.isBuildingCounsellingSummary) return;

    const confirmed = confirm(
      '⚠️ This will read ALL Counselling individual sessions, group sessions,\n' +
      'follow-up assessments, and session reviews, then write summary data\n' +
      'to the counsellingMonthlySummary collection.\n\n' +
      'It is safe to re-run — it fully overwrites existing summary data.\n\nContinue?'
    );
    if (!confirmed) return;

    this.isBuildingCounsellingSummary = true;
    this.counsellingSummaryBuildStatus = '⏳ Starting...';
    this.counsellingSummaryBuildProgress = 0;

    // In-memory accumulator: docId → summary data
    const summaryMap = new Map<string, any>();

    const toJsDate = (d: any): Date => {
      if (!d) return new Date();
      if (d?.toDate) return d.toDate();
      if (d instanceof Date) return d;
      if (d?.seconds) return new Date(d.seconds * 1000);
      return new Date(d);
    };

    const upsert = (
      counsellorName: string,
      centreCode: string,
      sessionDate: any,
      fields: { individual?: number; group?: number; followUp?: number; observed?: number }
    ) => {
      if (!counsellorName || !centreCode || !sessionDate) return;
      const date = toJsDate(sessionDate);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const yearMonth = `${year}-${String(month).padStart(2, '0')}`;
      const sanitized = counsellorName.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
      const docId = `${sanitized}_${yearMonth}_${centreCode}`;

      const existing = summaryMap.get(docId) || {
        counsellorName, centreCode, yearMonth, year, month,
        individualCount: 0, groupCount: 0, followUpCount: 0, observedCount: 0
      };
      if (fields.individual) existing.individualCount += fields.individual;
      if (fields.group) existing.groupCount += fields.group;
      if (fields.followUp) existing.followUpCount += fields.followUp;
      if (fields.observed) existing.observedCount += fields.observed;
      summaryMap.set(docId, existing);
    };

    try {
      // ── Step 1: Individual sessions (Counselling/allSessions/sessions) ──
      this.counsellingSummaryBuildStatus = '📊 Reading individual counselling sessions...';
      let lastDoc: any = null;
      let totalIndividual = 0;

      while (true) {
        let q: any = this.mFirestore.firestore
          .collection(`${this.env.counseling}/allSessions/sessions`)
          .orderBy('date')
          .limit(500);
        if (lastDoc) q = q.startAfter(lastDoc);

        const snap = await q.get();
        if (snap.empty) break;

        snap.docs.forEach((doc: any) => {
          const d = doc.data();
          if (!d.submittedBy || !d.date) return;
          const centreCode = d.centreCode || 'All';
          upsert(d.submittedBy, centreCode, d.date, { individual: 1 });
          totalIndividual++;
        });

        lastDoc = snap.docs[snap.docs.length - 1];
        this.counsellingSummaryBuildStatus = `📊 Individual sessions processed: ${totalIndividual}`;
        if (snap.docs.length < 500) break;
      }

      // ── Step 2: Group sessions (Counselling/allSessions/groupSessions) ──
      this.counsellingSummaryBuildStatus = '📊 Reading group counselling sessions...';
      lastDoc = null;
      let totalGroup = 0;

      while (true) {
        let q: any = this.mFirestore.firestore
          .collection(`${this.env.counseling}/allSessions/groupSessions`)
          .orderBy('date')
          .limit(500);
        if (lastDoc) q = q.startAfter(lastDoc);

        const snap = await q.get();
        if (snap.empty) break;

        snap.docs.forEach((doc: any) => {
          const d = doc.data();
          if (!d.submittedBy || !d.date) return;
          const centreCode = d.centreCode || 'All';
          upsert(d.submittedBy, centreCode, d.date, { group: 1 });
          totalGroup++;
        });

        lastDoc = snap.docs[snap.docs.length - 1];
        this.counsellingSummaryBuildStatus = `📊 Group sessions processed: ${totalGroup}`;
        if (snap.docs.length < 500) break;
      }

      // ── Step 3: Follow-up assessments (CounselingFollowUpAssessment) ──
      this.counsellingSummaryBuildStatus = '📊 Reading counselling follow-up assessments...';
      lastDoc = null;
      let totalFollowUp = 0;

      while (true) {
        let q: any = this.mFirestore.firestore
          .collection(this.env.counsellingFollowUp)
          .orderBy('date')
          .limit(500);
        if (lastDoc) q = q.startAfter(lastDoc);

        const snap = await q.get();
        if (snap.empty) break;

        snap.docs.forEach((doc: any) => {
          const d = doc.data();
          if (!d.submittedBy || !d.centreCode || !d.date) return;
          upsert(d.submittedBy, d.centreCode, d.date, { followUp: 1 });
          totalFollowUp++;
        });

        lastDoc = snap.docs[snap.docs.length - 1];
        this.counsellingSummaryBuildStatus = `📊 Follow-up assessments processed: ${totalFollowUp}`;
        if (snap.docs.length < 500) break;
      }

      // ── Step 4: Session observations (CounsellingSessionReviews) ──
      this.counsellingSummaryBuildStatus = '📊 Reading session observations...';
      lastDoc = null;
      let totalObserved = 0;

      while (true) {
        let q: any = this.mFirestore.firestore
          .collection(this.env.counsellingSessionReviews)
          .orderBy('date')
          .limit(500);
        if (lastDoc) q = q.startAfter(lastDoc);

        const snap = await q.get();
        if (snap.empty) break;

        snap.docs.forEach((doc: any) => {
          const d = doc.data();
          const observedBy = d.observedBy || d.submittedBy;
          if (!observedBy || !d.date) return;
          const centreCode = d.centreCode || 'All';
          upsert(observedBy, centreCode, d.date, { observed: 1 });
          totalObserved++;
        });

        lastDoc = snap.docs[snap.docs.length - 1];
        this.counsellingSummaryBuildStatus = `📊 Session observations processed: ${totalObserved}`;
        if (snap.docs.length < 500) break;
      }

      // ── Step 5: Write summary docs to Firestore ──
      const entries = Array.from(summaryMap.entries());
      this.counsellingSummaryBuildStatus = `💾 Writing ${entries.length} summary docs to Firestore...`;
      console.log(`📊 Total counselling summary docs to write: ${entries.length}`);

      const BATCH_SIZE = 400;
      for (let i = 0; i < entries.length; i += BATCH_SIZE) {
        const batch = this.mFirestore.firestore.batch();
        const chunk = entries.slice(i, i + BATCH_SIZE);
        chunk.forEach(([docId, data]) => {
          const ref = this.mFirestore.firestore
            .collection(this.env.counsellingMonthlySummary)
            .doc(docId);
          batch.set(ref, data); // Overwrite fully — safe to re-run
        });
        await batch.commit();
        this.counsellingSummaryBuildProgress = Math.round(((i + chunk.length) / entries.length) * 100);
        this.counsellingSummaryBuildStatus = `💾 Written ${Math.min(i + BATCH_SIZE, entries.length)} / ${entries.length} summary docs (${this.counsellingSummaryBuildProgress}%)`;
        console.log(this.counsellingSummaryBuildStatus);
      }

      this.counsellingSummaryBuildStatus =
        `✅ Done! Created ${entries.length} summary documents.\n` +
        `Individual: ${totalIndividual} | Group: ${totalGroup} | Follow-Up: ${totalFollowUp} | Observed: ${totalObserved}`;
      this.counsellingSummaryBuildProgress = 100;
      console.log('✅ buildCounsellingMonthlySummary complete');

    } catch (error: any) {
      this.counsellingSummaryBuildStatus = `❌ Error: ${error.message}`;
      console.error('❌ buildCounsellingMonthlySummary failed:', error);
    } finally {
      this.isBuildingCounsellingSummary = false;
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // DEV SEEDING: Copy production data to dev collections for local testing.
  //   ALChild (all)                    → devChild
  //   educationTest (latest 200)       → educationTesting
  //   Counselling sessions (latest 200)→ counsellingTest/allSessions/sessions
  //   ALUnits (all, Firestore)            → ALUnits2
  //   + every other collection wired to environment.testMode/productionMode
  //     (see extraSeeds below), each capped and skipped when prod === test.
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Copies documents from a production collection into its test counterpart.
   * Skips entirely when prodPath === testPath (nothing to do). Shallow copy
   * only — subcollections of copied documents are not recursed into.
   */
  private async seedCollection(
    label: string,
    prodPath: string,
    testPath: string,
    opts?: { limit?: number; orderByField?: string }
  ): Promise<number> {
    if (prodPath === testPath) {
      console.log(`⏭️ Skipping ${label}: prod and test collection names are identical`);
      return 0;
    }

    this.devEntriesStatus = `📦 Copying ${label}…`;

    let query: any = this.mFirestore.firestore.collection(prodPath);
    if (opts?.orderByField) query = query.orderBy(opts.orderByField, 'desc');
    if (opts?.limit) query = query.limit(opts.limit);

    const snap = await query.get();
    if (snap.empty) {
      console.log(`ℹ️ ${label}: source collection empty, nothing to copy`);
      return 0;
    }

    const BATCH_SIZE = 400;
    let batch = this.mFirestore.firestore.batch();
    let batchCount = 0;
    let copied = 0;

    for (const doc of snap.docs) {
      batch.set(this.mFirestore.firestore.collection(testPath).doc(doc.id), doc.data());
      batchCount++;

      if (batchCount >= BATCH_SIZE) {
        await batch.commit();
        copied += batchCount;
        batch = this.mFirestore.firestore.batch();
        batchCount = 0;
      }
    }

    if (batchCount > 0) {
      await batch.commit();
      copied += batchCount;
    }

    console.log(`✅ ${label}: ${copied} docs copied (${prodPath} → ${testPath})`);
    return copied;
  }

  async DevEntries() {
    if (this.isRunningDevEntries) return;

    const confirmed = confirm(
      '⚠️ This will copy PRODUCTION data into dev collections:\n\n' +
      '  • ALL ALChild docs                    → devChild\n' +
      '  • ALL ALCentres docs                  → centres\n' +
      '  • Latest 200 educationTest            → educationTesting\n' +
      '  • Latest 200 educationGroupTest       → educationGroupTesting\n' +
      '  • Latest 200 counselling sessions     → counsellingTest/allSessions/sessions\n' +
      '  • Latest 200 counselling group sess.  → counsellingTest/allSessions/groupSessions\n' +
      '  • ALL EducationImpactReport           → testEducationImpactReport\n' +
      '  • ALL ALUnits (Firestore)                → ALUnits2\n' +
      '  • + ~55 more collections (vehicles, tasks, notifications, logs,\n' +
      '    presentations, observations, follow-ups, etc.) — capped and\n' +
      '    ordered where the collection can grow large. See console for\n' +
      '    a per-collection summary once seeding finishes.\n\n' +
      'Existing dev data will be overwritten.\n\nContinue?'
    );
    if (!confirmed) return;

    this.isRunningDevEntries = true;
    this.devEntriesStatus = '⏳ Starting…';
    this.devEntriesProgress = 0;

    try {
      // ── Step 1: ALChild → devChild ──────────────────────────────────────
      if (environment.productionMode.child === environment.testMode.child) {
        console.log('⏭️ Skipping ALChild → devChild: prod and test collection names are identical');
      } else {
        this.devEntriesStatus = '👶 Fetching ALChild…';
        const childSnap = await this.mFirestore.collection(environment.productionMode.child).get().toPromise();

        if (childSnap && !childSnap.empty) {
          const BATCH_SIZE = 400;
          let batch = this.mFirestore.firestore.batch();
          let batchCount = 0;
          let copied = 0;

          for (const doc of childSnap.docs) {
            batch.set(this.mFirestore.collection(environment.testMode.child).doc(doc.id).ref, doc.data() as any);
            batchCount++;

            if (batchCount >= BATCH_SIZE) {
              await batch.commit();
              copied += batchCount;
              batch = this.mFirestore.firestore.batch();
              batchCount = 0;
              this.devEntriesStatus = `👶 ALChild → devChild: ${copied} / ${childSnap.docs.length} copied…`;
            }
          }

          if (batchCount > 0) {
            await batch.commit();
            copied += batchCount;
          }

          console.log(`✅ ALChild → devChild: ${copied} docs`);
        }
      }
      this.devEntriesProgress = 3;

      // ── Step 2: ALCentres → centres ─────────────────────────────────────
      if (environment.productionMode.centres === environment.testMode.centres) {
        console.log('⏭️ Skipping ALCentres → centres: prod and test collection names are identical');
      } else {
        this.devEntriesStatus = '🏛️ Fetching ALCentres…';
        const centresSnap = await this.mFirestore.collection(environment.productionMode.centres).get().toPromise();

        if (centresSnap && !centresSnap.empty) {
          const BATCH_SIZE = 400;
          let batch = this.mFirestore.firestore.batch();
          let batchCount = 0;
          let copied = 0;

          for (const doc of centresSnap.docs) {
            batch.set(this.mFirestore.collection(environment.testMode.centres).doc(doc.id).ref, doc.data() as any);
            batchCount++;

            if (batchCount >= BATCH_SIZE) {
              await batch.commit();
              copied += batchCount;
              batch = this.mFirestore.firestore.batch();
              batchCount = 0;
            }
          }

          if (batchCount > 0) {
            await batch.commit();
            copied += batchCount;
          }

          console.log(`✅ ALCentres → centres: ${copied} docs`);
        }
      }
      this.devEntriesProgress = 6;

      // ── Step 3: educationTest (latest 200) → educationTesting ───────────
      if (environment.productionMode.education === environment.testMode.education) {
        console.log('⏭️ Skipping educationTest → educationTesting: prod and test collection names are identical');
      } else {
        this.devEntriesStatus = '📚 Copying latest 200 educationTest → educationTesting…';
        const eduSnap = await this.mFirestore.firestore
          .collection(environment.productionMode.education)
          .orderBy('sessionDate', 'desc')
          .limit(200)
          .get();

        if (!eduSnap.empty) {
          const batch = this.mFirestore.firestore.batch();
          eduSnap.docs.forEach(doc => {
            batch.set(this.mFirestore.collection(environment.testMode.education).doc(doc.id).ref, doc.data());
          });
          await batch.commit();
          console.log(`✅ educationTest → educationTesting: ${eduSnap.docs.length} docs`);
        }
      }
      this.devEntriesProgress = 9;

      // ── Step 4: educationGroupTest (latest 200) → educationGroupTesting ──
      if (environment.productionMode.educationGroup === environment.testMode.educationGroup) {
        console.log('⏭️ Skipping educationGroupTest → educationGroupTesting: prod and test collection names are identical');
      } else {
        this.devEntriesStatus = '📚 Copying latest 200 educationGroupTest → educationGroupTesting…';
        const eduGroupSnap = await this.mFirestore.firestore
          .collection(environment.productionMode.educationGroup)
          .orderBy('sessionDate', 'desc')
          .limit(200)
          .get();

        if (!eduGroupSnap.empty) {
          const batch = this.mFirestore.firestore.batch();
          eduGroupSnap.docs.forEach(doc => {
            batch.set(this.mFirestore.collection(environment.testMode.educationGroup).doc(doc.id).ref, doc.data());
          });
          await batch.commit();
          console.log(`✅ educationGroupTest → educationGroupTesting: ${eduGroupSnap.docs.length} docs`);
        }
      }
      this.devEntriesProgress = 12;

      // ── Step 5: Counselling sessions (latest 200) → counsellingTest ──────
      if (environment.productionMode.counseling === environment.testMode.counseling) {
        console.log('⏭️ Skipping counselling sessions copy: prod and test collection names are identical');
      } else {
        this.devEntriesStatus = '💬 Copying latest 200 counselling sessions → counsellingTest…';
        const counselSnap = await this.mFirestore.firestore
          .collection(`${environment.productionMode.counseling}/allSessions/sessions`)
          .orderBy('date', 'desc')
          .limit(200)
          .get();

        if (!counselSnap.empty) {
          const batch = this.mFirestore.firestore.batch();
          counselSnap.docs.forEach(doc => {
            const ref = this.mFirestore.firestore
              .collection(`${environment.testMode.counseling}/allSessions/sessions`)
              .doc(doc.id);
            batch.set(ref, doc.data());
          });
          await batch.commit();
          console.log(`✅ Counselling → counsellingTest: ${counselSnap.docs.length} docs`);
        }
      }
      this.devEntriesProgress = 15;

      // ── Step 6: Counselling group sessions (latest 200) → counsellingTest ─
      if (environment.productionMode.counseling === environment.testMode.counseling) {
        console.log('⏭️ Skipping counselling group sessions copy: prod and test collection names are identical');
      } else {
        this.devEntriesStatus = '💬 Copying latest 200 counselling group sessions → counsellingTest…';
        const counselGroupSnap = await this.mFirestore.firestore
          .collection(`${environment.productionMode.counseling}/allSessions/groupSessions`)
          .orderBy('date', 'desc')
          .limit(200)
          .get();

        if (!counselGroupSnap.empty) {
          const batch = this.mFirestore.firestore.batch();
          counselGroupSnap.docs.forEach(doc => {
            const ref = this.mFirestore.firestore
              .collection(`${environment.testMode.counseling}/allSessions/groupSessions`)
              .doc(doc.id);
            batch.set(ref, doc.data());
          });
          await batch.commit();
          console.log(`✅ Counselling groupSessions → counsellingTest: ${counselGroupSnap.docs.length} docs`);
        }
      }
      this.devEntriesProgress = 18;

      // ── Step 7: EducationImpactReport → testEducationImpactReport ────────
      if (environment.productionMode.educationImpactReport === environment.testMode.educationImpactReport) {
        console.log('⏭️ Skipping EducationImpactReport → testEducationImpactReport: prod and test collection names are identical');
      } else {
        this.devEntriesStatus = '📊 Copying EducationImpactReport → testEducationImpactReport…';
        const impactSnap = await this.mFirestore.collection(environment.productionMode.educationImpactReport).get().toPromise();

        if (impactSnap && !impactSnap.empty) {
          const BATCH_SIZE = 400;
          let batch = this.mFirestore.firestore.batch();
          let batchCount = 0;
          let copied = 0;

          for (const doc of impactSnap.docs) {
            batch.set(this.mFirestore.collection(environment.testMode.educationImpactReport).doc(doc.id).ref, doc.data() as any);
            batchCount++;

            if (batchCount >= BATCH_SIZE) {
              await batch.commit();
              copied += batchCount;
              batch = this.mFirestore.firestore.batch();
              batchCount = 0;
            }
          }

          if (batchCount > 0) {
            await batch.commit();
            copied += batchCount;
          }

          console.log(`✅ EducationImpactReport → testEducationImpactReport: ${copied} docs`);
        }
      }
      this.devEntriesProgress = 21;

      // ── Step 8: ALUnits → ALUnits2 (Firestore, {collection}/{centre}/units/{key}) ──
      if (environment.productionMode.units === environment.testMode.units) {
        console.log('⏭️ Skipping ALUnits → ALUnits2: prod and test collections are identical');
      } else {
        this.devEntriesStatus = '🏢 Copying ALUnits → ALUnits2 (Firestore)…';
        const fsDb = this.mFirestore.firestore;
        // Parent docs under the units collection don't exist (only the `units` subcollections do),
        // so take the centre codes from the prod centres collection.
        const centreSnap = await fsDb.collection(environment.productionMode.centres).get();
        const centreCodes = centreSnap.docs.map(d => d.id);
        let unitsCopied = 0;
        for (const centreCode of centreCodes) {
          const units = await fsDb.collection(`${environment.productionMode.units}/${centreCode}/units`).get();
          for (let i = 0; i < units.docs.length; i += 400) {
            const batch = fsDb.batch();
            units.docs.slice(i, i + 400).forEach(d =>
              batch.set(fsDb.doc(`${environment.testMode.units}/${centreCode}/units/${d.id}`), d.data()));
            await batch.commit();
          }
          unitsCopied += units.size;
        }
        if (unitsCopied) {
          console.log(`✅ ALUnits → ALUnits2 copied (${unitsCopied} units)`);
        } else {
          console.warn('ALUnits (Firestore) was empty — nothing copied. Run Migrate Units first.');
        }
      }
      this.devEntriesProgress = 25;

      // ── Steps 9+: every other collection wired to environment config ────
      // Each entry copies (optionally capped/ordered) prod docs into the
      // matching test collection; entries where prod === test are skipped
      // automatically by seedCollection().
      const extraSeeds: { label: string; prodPath: string; testPath: string; limit?: number; orderByField?: string }[] = [
        {label: 'Vehicles', prodPath: environment.productionMode.vehicles, testPath: environment.testMode.vehicles},
        {label: 'Drivers', prodPath: environment.productionMode.drivers, testPath: environment.testMode.drivers},
        {label: 'Trips', prodPath: environment.productionMode.trips, testPath: environment.testMode.trips, limit: 300, orderByField: 'trip_started_at'},
        {label: 'Petrol Filling', prodPath: environment.productionMode.petrolFilling, testPath: environment.testMode.petrolFilling, limit: 300},
        {label: 'Vehicle Servicing', prodPath: environment.productionMode.vehicleServicing, testPath: environment.testMode.vehicleServicing, limit: 300},
        {label: 'Tickets Raised', prodPath: environment.productionMode.ticketsRaised, testPath: environment.testMode.ticketsRaised, limit: 300, orderByField: 'createdAt'},
        {label: 'Counselling Follow-Up Assessments', prodPath: environment.productionMode.counsellingFollowUp, testPath: environment.testMode.counsellingFollowUp, limit: 200, orderByField: 'date'},
        {label: 'Education Follow-Up Assessments', prodPath: environment.productionMode.educationFollowUp, testPath: environment.testMode.educationFollowUp, limit: 200, orderByField: 'date'},
        {label: 'Counselling Impact Reports', prodPath: environment.productionMode.counsellingImpactReport, testPath: environment.testMode.counsellingImpactReport, limit: 200, orderByField: 'createdAt'},
        {label: 'Child Case Studies', prodPath: environment.productionMode.childCaseStudy, testPath: environment.testMode.childCaseStudy, limit: 200},
        {label: 'Report Cards', prodPath: environment.productionMode.reportCard, testPath: environment.testMode.reportCard, limit: 200, orderByField: 'date'},
        {label: 'Counselling SOP', prodPath: environment.productionMode.counsellingSOP, testPath: environment.testMode.counsellingSOP},
        {label: 'Education SOP', prodPath: environment.productionMode.educationSOP, testPath: environment.testMode.educationSOP},
        {label: 'Counselling Session Reviews', prodPath: environment.productionMode.counsellingSessionReviews, testPath: environment.testMode.counsellingSessionReviews, limit: 200},
        {label: 'Counselling Centre Log', prodPath: environment.productionMode.counsellingCenterLog, testPath: environment.testMode.counsellingCenterLog, limit: 200, orderByField: 'date'},
        {label: 'Education Centre Log', prodPath: environment.productionMode.educationCenterLog, testPath: environment.testMode.educationCenterLog, limit: 200, orderByField: 'date'},
        {label: 'Counselling Presentation List', prodPath: environment.productionMode.counsellingPresentationList, testPath: environment.testMode.counsellingPresentationList, limit: 200, orderByField: 'createdAt'},
        {label: 'Education Presentation List', prodPath: environment.productionMode.educationPresentationList, testPath: environment.testMode.educationPresentationList, limit: 200, orderByField: 'createdAt'},
        {label: 'Om Shanti Requests', prodPath: environment.productionMode.omShantiRequest, testPath: environment.testMode.omShantiRequest, limit: 300},
        {label: 'Child Highlights', prodPath: environment.productionMode.highlights, testPath: environment.testMode.highlights, limit: 200, orderByField: 'eventDate'},
        {label: 'Centre Highlights', prodPath: environment.productionMode.centreHighlights, testPath: environment.testMode.centreHighlights, limit: 200, orderByField: 'eventDate'},
        {label: 'Donations', prodPath: environment.productionMode.donations, testPath: environment.testMode.donations, limit: 300},
        {label: 'Attempted Donations', prodPath: environment.productionMode.attemptedDonations, testPath: environment.testMode.attemptedDonations, limit: 300},
        {label: 'Failed Donations', prodPath: environment.productionMode.failedDonations, testPath: environment.testMode.failedDonations, limit: 300},
        {label: 'User Permissions', prodPath: environment.productionMode.userPermissions, testPath: environment.testMode.userPermissions},
        {label: '8080 Inquiries', prodPath: environment.productionMode.inquiry8080, testPath: environment.testMode.inquiry8080, limit: 300},
        {label: 'Glossary', prodPath: environment.productionMode.glossary, testPath: environment.testMode.glossary},
        {label: 'Centre Rankings', prodPath: environment.productionMode.centreRankings, testPath: environment.testMode.centreRankings},
        {label: 'Bulk Ranking (legacy)', prodPath: environment.productionMode.bulkRanking, testPath: environment.testMode.bulkRanking},
        {label: 'Prefixes', prodPath: environment.productionMode.prefixes, testPath: environment.testMode.prefixes},
        {label: 'Minutes of Meetings', prodPath: environment.productionMode.minutesOfMeetings, testPath: environment.testMode.minutesOfMeetings, limit: 300},
        {label: 'Change Logs', prodPath: environment.productionMode.changeLogs, testPath: environment.testMode.changeLogs, limit: 200, orderByField: 'date'},
        {label: 'Staff Learning Sessions', prodPath: environment.productionMode.staffLearningSessions, testPath: environment.testMode.staffLearningSessions, limit: 300},
        {label: 'Meal Mate Photo Emails', prodPath: environment.productionMode.mealMatePhotoEmail, testPath: environment.testMode.mealMatePhotoEmail, limit: 300},
        {label: 'Inventory Taxonomy Backups', prodPath: environment.productionMode.inventoryTaxonomyBackups, testPath: environment.testMode.inventoryTaxonomyBackups},
        {label: 'Counselling Monthly Summary', prodPath: environment.productionMode.counsellingMonthlySummary, testPath: environment.testMode.counsellingMonthlySummary, limit: 500},
        {label: 'Education Monthly Summary', prodPath: environment.productionMode.educationMonthlySummary, testPath: environment.testMode.educationMonthlySummary, limit: 500},
        {label: 'Admission Logs', prodPath: environment.productionMode.admissionLogs, testPath: environment.testMode.admissionLogs, limit: 300},
        {label: 'Inventory (New/Migration)', prodPath: environment.productionMode.inventoryNew, testPath: environment.testMode.inventoryNew},
        {label: 'Education Observations', prodPath: environment.productionMode.educationObservation, testPath: environment.testMode.educationObservation, limit: 200, orderByField: 'sessionDate'},
        {label: 'Counsellor Observations', prodPath: environment.productionMode.counsellorObservation, testPath: environment.testMode.counsellorObservation, limit: 200, orderByField: 'sessionDate'},
        {label: 'Counselling Work Updates', prodPath: environment.productionMode.counsellingWorkUpdates, testPath: environment.testMode.counsellingWorkUpdates, limit: 200, orderByField: 'date'},
        {label: 'Child Follow-Up Feedback', prodPath: environment.productionMode.childFollowUpFeedback, testPath: environment.testMode.childFollowUpFeedback, limit: 300},
        {label: 'Advance Mealmate Bookings', prodPath: environment.productionMode.advanceMealmateBookings, testPath: environment.testMode.advanceMealmateBookings, limit: 200, orderByField: 'createdAt'},
        {label: 'Education Questions List', prodPath: environment.productionMode.educationQuestionsList, testPath: environment.testMode.educationQuestionsList},
        {label: 'Counselling Questions List', prodPath: environment.productionMode.counsellingQuestionsList, testPath: environment.testMode.counsellingQuestionsList},
        {label: 'SOP Resource Repository', prodPath: environment.productionMode.resourceRepository, testPath: environment.testMode.resourceRepository},
        {label: 'Counselling Logs', prodPath: environment.productionMode.counsellingLogs, testPath: environment.testMode.counsellingLogs, limit: 300},
        {label: 'Education Logs', prodPath: environment.productionMode.educationLogs, testPath: environment.testMode.educationLogs, limit: 300},
        {label: 'Tasks', prodPath: environment.productionMode.tasks, testPath: environment.testMode.tasks, limit: 300},
        {label: 'Projects', prodPath: environment.productionMode.projects, testPath: environment.testMode.projects},
        {label: 'Contact', prodPath: environment.productionMode.contact, testPath: environment.testMode.contact},
        {label: 'Dashboard Counters', prodPath: environment.productionMode.dashboard, testPath: environment.testMode.dashboard},
        {label: 'Counselling Upload Test (legacy)', prodPath: environment.productionMode.counsellingUploadTest, testPath: environment.testMode.counsellingUploadTest},
        {label: 'Notifications', prodPath: environment.productionMode.notifications, testPath: environment.testMode.notifications, limit: 500, orderByField: 'time'},
      ];

      for (let i = 0; i < extraSeeds.length; i++) {
        const seed = extraSeeds[i];
        try {
          await this.seedCollection(seed.label, seed.prodPath, seed.testPath, {
            limit: seed.limit,
            orderByField: seed.orderByField
          });
        } catch (seedError: any) {
          console.error(`❌ Failed to seed ${seed.label}:`, seedError);
        }
        this.devEntriesProgress = 25 + Math.round(((i + 1) / extraSeeds.length) * 75);
      }

      this.devEntriesStatus = '✅ Dev seeding complete! All production data copied to dev collections.';
      alert('DevEntries complete!\n\nProduction data has been seeded into dev collections.');

    } catch (error: any) {
      this.devEntriesStatus = `❌ Error: ${error?.message ?? error}`;
      console.error('DevEntries failed:', error);
      alert(`DevEntries error: ${error?.message ?? error}`);
    } finally {
      this.isRunningDevEntries = false;
    }
  }
}
